import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dcLineGinCode, dcLinePendingQty, dcLineReceivedQty, fmtQty, isDcLineReceived } from "@/lib/dcGinProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Plus, ArrowRight, Trash2, History, Hash, User as UserIcon, ShieldCheck, ShieldAlert, Clock, CheckCircle2, XCircle, Package, Send, Pencil, Ruler } from "lucide-react";
import { toast } from "sonner";
import ReceivedPartPicker from "@/components/ReceivedPartPicker";

const STAGES = [
  { key: "machine_shop",     label: "Machine Shop",     store: "MS_STORE",     tone: "bg-amber-50 border-amber-200 text-amber-800" },
  { key: "impregnation",     label: "Impregnation",     store: "IMP_STORE",    tone: "bg-teal-50 border-teal-200 text-teal-800" },
  { key: "fabrication",      label: "Fabrication",      store: "FAB_STORE",    tone: "bg-cyan-50 border-cyan-200 text-cyan-800" },
  { key: "powder_coating",   label: "Powder Coating",   store: "PWD_STORE",    tone: "bg-purple-50 border-purple-200 text-purple-800" },
  { key: "semi_finished",    label: "Semi Finished",    store: "SFG_STORE",    tone: "bg-indigo-50 border-indigo-200 text-indigo-800" },
  { key: "assembly",         label: "Assembly",         store: "ASM_STORE",    tone: "bg-sky-50 border-sky-200 text-sky-800" },
  { key: "testing",          label: "Testing",          store: "TEST_STORE",   tone: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800" },
  { key: "final_inspection", label: "Final Inspection", store: "FI_STORE",     tone: "bg-orange-50 border-orange-200 text-orange-800" },
  { key: "finished",         label: "Finished Goods",   store: "FG_STORE",     tone: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  { key: "rework",           label: "Rework",           store: "REWORK_STORE", tone: "bg-rose-50 border-rose-200 text-rose-800" },
];

const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));
// "rejected" is a terminal, system-only destination (a second QC failure of an
// already-reworked batch at Testing/Final Inspection) — never a manual
// stage-picker option, so it's a display-only label, not part of STAGES.
STAGE_LABEL.rejected = "Rejected Warehouse";
const STAGE_STORE = Object.fromEntries(STAGES.map((s) => [s.key, s.store]));
// Default Issue destination overrides (mirrors backend NEXT_STAGE): Semi
// Finished parts go straight to Final Inspection rather than Assembly, and
// Assembly goes straight to Final Inspection rather than Testing.
// Skipped stages stay selectable in the Issue dialog as explicit choices.
// Moving to this default stop is the normal flow, not a "skip".
const DEFAULT_NEXT_STAGE = { semi_finished: "final_inspection", assembly: "final_inspection" };
const isDefaultNext = (from, to) => DEFAULT_NEXT_STAGE[from] === to;
const ORDERED_STAGE_KEYS = STAGES.filter((s) => s.key !== "rework").map((s) => s.key); // machine_shop … finished

// Raw Material — deliberately NOT part of STAGES: it precedes the job
// pipeline (no production job exists yet, just RM Store stock), so it's
// never a job.current_stage and never a valid Send-for-QC/Issue-to-Stage
// destination. Its single-stage page lists RM Store items instead of jobs.
const RAW_MATERIAL_STAGE = { key: "raw_material", label: "Raw Material", store: "RM_STORE", tone: "bg-lime-50 border-lime-200 text-lime-800" };

// Issue-to-Stage destination: always the next stage in the Production Flow
// (Raw Material → Machine Shop → … → Finished Goods). Finished Goods is the
// last stage, so it has no destination ("").
const ISSUE_FLOW_KEYS = [RAW_MATERIAL_STAGE.key, ...ORDERED_STAGE_KEYS];
const STORE_STAGE = { [RAW_MATERIAL_STAGE.store]: RAW_MATERIAL_STAGE.key, ...Object.fromEntries(STAGES.map((s) => [s.store, s.key])) };
const nextFlowStage = (stageKey) => {
  const idx = ISSUE_FLOW_KEYS.indexOf(stageKey);
  return idx >= 0 ? ISSUE_FLOW_KEYS[idx + 1] || "" : "";
};
// Source stage = the stage owning the chosen source store when it's part of
// the flow, otherwise the stage the dialog was opened from.
const issueDestFor = (sourceStore, fallbackStageKey) => {
  const src = ISSUE_FLOW_KEYS.includes(STORE_STAGE[sourceStore]) ? STORE_STAGE[sourceStore] : fallbackStageKey;
  return nextFlowStage(src);
};

// Item type expected at the OUTPUT of each stage (Phase 28 · Fabrication added Phase 33)
const STAGE_ITEM_TYPE = {
  machine_shop:     "MS",
  impregnation:     "IMP",
  fabrication:      "FAB",
  powder_coating:   "PWD",
  semi_finished:    "SFG",
  assembly:         "ASM",
  testing:          "TEST",
  final_inspection: "FI",
  finished:         "FG",
  rework:           "RWK",
};

// Ordered stores for the destination dropdown
const ALL_STORES = [
  { key: "RM_STORE",     label: "RM Store" },
  { key: "MS_STORE",     label: "Machine Shop Store" },
  { key: "IMP_STORE",    label: "Impregnation Store" },
  { key: "FAB_STORE",    label: "Fabrication Store" },
  { key: "PWD_STORE",    label: "Powder Coating Store" },
  { key: "SFG_STORE",    label: "Semi-Finished Store" },
  { key: "ASM_STORE",    label: "Assembly Store" },
  { key: "TEST_STORE",   label: "Testing Store" },
  { key: "FI_STORE",     label: "Final Inspection Store" },
  { key: "FG_STORE",     label: "Finished Goods Store" },
  { key: "REWORK_STORE", label: "Rework Store" },
];

export default function ProductionFlowPage() {
  // Single-stage mode: reached via the sidebar's Production Flow submenu
  // (/production-flow/:stage). An unknown stage slug bounces back to the
  // all-stages overview rather than showing a broken page.
  const { stage: stageParam } = useParams();
  const navigate = useNavigate();
  const isRawMaterial = stageParam === "raw_material";
  const currentStage = isRawMaterial ? RAW_MATERIAL_STAGE : (stageParam ? STAGES.find((s) => s.key === stageParam) : null);
  const isSingleStage = !!currentStage;
  useEffect(() => {
    if (stageParam && !currentStage) navigate("/production-flow", { replace: true });
  }, [stageParam, currentStage, navigate]);

  const [jobs, setJobs] = useState([]);
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [qcLog, setQcLog] = useState([]);
  const [issueOpen, setIssueOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 33 — per-stage real-time search. Declared early: the Raw Material
  // block below reads stageSearch.raw_material inside a useMemo dependency
  // array, which is evaluated eagerly at that line (not deferred like the
  // memo body), so this must exist before that point or it's a TDZ crash.
  const [stageSearch, setStageSearch] = useState({});
  // Per-stage record filter (status + started-on date range) for the
  // single-stage views. Work stages default to "Active only" so the page
  // shows just the open work — closed / cancelled history is one click away
  // (Status filter or "Load All"). Finished Goods defaults to "all" since
  // every batch there is closed by definition.
  const ALL_STAGE_FILTER = { status: "all", from: "", to: "" };
  const defaultStageFilter = (stageKey) =>
    stageKey === "finished" ? ALL_STAGE_FILTER : { status: "active", from: "", to: "" };
  const [stageFilters, setStageFilters] = useState({});
  const getStageFilter = (stageKey) => stageFilters[stageKey] || defaultStageFilter(stageKey);
  const setStageFilter = (stageKey, patch) =>
    setStageFilters((f) => ({ ...f, [stageKey]: { ...getStageFilter(stageKey), ...patch } }));
  const clearStageFilter = (stageKey) =>
    setStageFilters((f) => ({ ...f, [stageKey]: ALL_STAGE_FILTER }));
  const isStageFilterActive = (stageKey) => {
    const f = getStageFilter(stageKey);
    return f.status !== "all" || !!f.from || !!f.to;
  };
  // Phase 35.4 — subcontract-outward state
  const [suppliers, setSuppliers] = useState([]);
  const [subcontractDcs, setSubcontractDcs] = useState([]);   // active (dispatched) outward DCs
  const [sendOpen, setSendOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  // Phase 35.5 — "Send Outside" is now a multi-select action: scStage is the
  // stage column the operator opened it from; scSelection maps job_id → qty
  // for every job currently ticked in the list.
  const [scStage, setScStage] = useState(null);
  const [scSelection, setScSelection] = useState({});
  const [scDc, setScDc] = useState(null);
  const [scForm, setScForm] = useState({ vendor_id: "", vehicle_no: "", notes: "" });
  const [scReceiveForm, setScReceiveForm] = useState({ received_qtys: [], received_item_ids: [], target_stores: [], notes: "" });

  const rawItems = useMemo(() => items.filter((i) => i.type === "RAW"), [items]);
  const fgItems = useMemo(() => items.filter((i) => i.type !== "RAW"), [items]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  // Routing (per-FG stage → output part map, set in the Routing editor) —
  // {fg_item_id: routing_doc}. When a job's FG has a routing with at least
  // one stage filled in, it's authoritative for which forward stage comes
  // next (blank stages are skipped outright, never offered as a
  // destination) and which output part number that stage defaults to.
  const [routings, setRoutings] = useState([]);
  const routingByFg = useMemo(() => Object.fromEntries(routings.map((r) => [r.item_id, r])), [routings]);
  const isRoutingConfigured = (routing) => !!routing && Object.values(routing.stage_outputs || {}).some(Boolean);
  // Mirrors the backend's `_resolve_job_fg`: the issued/output item's own
  // "Linked Finished Good" first, then the job's own fg_item_id.
  const resolveJobFg = (job) => {
    const linkedVia = (itemId) => {
      const parent = itemMap[itemMap[itemId]?.parent_fg_item_id];
      return parent && (parent.type === "FG" || parent.type === "SFG") ? parent.id : null;
    };
    const linked = linkedVia(job.current_item_id) || linkedVia(job.raw_item_id);
    if (linked) return linked;
    const jobFg = itemMap[job.fg_item_id];
    return jobFg && (jobFg.type === "FG" || jobFg.type === "SFG") ? job.fg_item_id : null;
  };
  const routingForJob = (job) => routingByFg[resolveJobFg(job)] || null;
  // Forward stages for `fromStageKey`, Routing-filtered when configured:
  // only stages with a non-blank stage_outputs entry are offered, in order
  // — a blank stage is skipped outright rather than needing a manual
  // "Skip Stage" override. Falls back to every forward stage (the pre-
  // Routing behavior) when this FG has no routing configured.
  const routingForwardStages = (routing, fromStageKey) => {
    const curIdx = STAGES.findIndex((s) => s.key === fromStageKey);
    const forward = STAGES.slice(curIdx + 1).filter((s) => s.key !== "rework" && s.key !== "finished");
    const finishedStage = STAGES.find((s) => s.key === "finished");
    if (finishedStage) forward.push(finishedStage);
    if (!isRoutingConfigured(routing)) return forward;
    return forward.filter((s) => routing.stage_outputs?.[s.key]);
  };

  // Job Work DC dialogs (both the job-based "Send Outside" list and the Raw
  // Material one) render one "Return as part #" <Select> PER ROW. Rendering
  // the FULL items catalog (this ERP's runs into the thousands once every
  // part-number/size variant is counted) as every single row's option list
  // is what made the dialog agonizingly slow to open and threw off Radix's
  // popper positioning enough to visibly overflow its box (a huge initially
  // -mounted option list confuses the trigger's width/measurement) — the
  // "fields coming out" symptom. Two changes fix both at once:
  //   1. Each row's options are capped to the type of item this stage
  //      actually produces (STAGE_ITEM_TYPE[scStage] — the same rule
  //      already used to pick the QC-forward output part elsewhere in this
  //      file), which is both dramatically smaller AND the only choice that
  //      makes business sense — a Job Work DC is a round trip, not a part
  //      transformation into an unrelated category.
  //   2. The (still-shared, memoized) option list is computed ONCE per
  //      dialog, not once per row, so 39 rows don't each pay for it.
  const MAX_DC_OUTPUT_OPTIONS = 300;
  const scExpectedType = STAGE_ITEM_TYPE[scStage] || "";
  const scDcItemOptions = useMemo(() => {
    const pool = (scExpectedType ? items.filter((i) => i.type === scExpectedType) : items).slice(0, MAX_DC_OUTPUT_OPTIONS);
    return pool.map((opt) => (
      <SelectItem key={opt.id} value={opt.id} data-testid={`item-opt-${opt.code}`}>{opt.code} — {opt.name}</SelectItem>
    ));
  }, [items, scExpectedType]);
  // Raw Material's output part genuinely can be any type (raw bar in,
  // machined part back), so it isn't narrowed by type — but every row's own
  // (RAW) item still needs to always be a valid choice, so all RAW items are
  // kept in full and only the remaining, larger non-RAW catalog is capped.
  const rawDcItemOptions = useMemo(() => {
    const nonRaw = items.filter((i) => i.type !== "RAW").slice(0, MAX_DC_OUTPUT_OPTIONS);
    return [...rawItems, ...nonRaw].map((opt) => (
      <SelectItem key={opt.id} value={opt.id} data-testid={`item-opt-${opt.code}`}>{opt.code} — {opt.name}</SelectItem>
    ));
  }, [items, rawItems]);

  const rmStock = (itemId) => {
    const r = inventory.find((x) => x.item_id === itemId && x.warehouse === "RM_STORE");
    return r ? r.qty : 0;
  };

  const [issueForm, setIssueForm] = useState({ raw_item_id: "", fg_item_id: "", raw_qty: 0, batch_no: "", operator: "", machine: "", expected_completion: "" });

  const load = async () => {
    const [j, i, inv, qcs, pend, sup, dcs, rts] = await Promise.all([
      api.get("/production-jobs"),
      api.get("/items"),
      api.get("/inventory"),
      // A 30-day cutoff here silently downgraded the "QC Passed/Failed"
      // badge for any job whose stage QC was decided further back than that
      // to a plain "In Process" — the record was never missing, just outside
      // this hard-coded window. A ~100-year window is effectively "no cutoff".
      api.get("/qc-inspections?days=36500").catch(() => ({ data: [] })),
      api.get("/quality/inspections/pending").catch(() => ({ data: [] })),
      api.get("/suppliers").catch(() => ({ data: [] })),
      api.get("/delivery-challans?kind=subcontract").catch(() => ({ data: [] })),
      api.get("/routings").catch(() => ({ data: [] })),
    ]);
    setJobs(j.data);
    setItems(i.data);
    setInventory(inv.data);
    setQcLog(qcs.data || []);
    setPendingInsp(pend.data || []);
    setSuppliers(sup.data || []);
    setSubcontractDcs((dcs.data || []).filter((d) => d.status === "dispatched"));
    setRoutings(rts.data || []);
  };
  useEffect(() => { load(); }, []);

  const [pendingInsp, setPendingInsp] = useState([]);
  // Phase 29 — in-flight (pending-inspection) qty per job
  const inflightByJob = useMemo(() => {
    const map = {};
    pendingInsp.forEach((p) => {
      const q = Number(p.pending_qty || 0);
      map[p.job_id] = (map[p.job_id] || 0) + q;
    });
    return map;
  }, [pendingInsp]);

  // Count of pending-arrival (unreceived) jobs per stage — drives the badge
  // on each single-stage page's "Receive" button.
  const pendingReceiveCountByStage = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      if (!!j.parent_job_id && !j.received_at && j.status === "active" && j.current_stage !== "rework" && j.current_stage !== "finished") {
        map[j.current_stage] = (map[j.current_stage] || 0) + 1;
      }
    });
    return map;
  }, [jobs]);

  // Per-job latest COMPLETED QC record (per stage) → drives the "QC Passed / Failed" badge
  const latestCompletedQc = useMemo(() => {
    const map = {}; // {job_id: {stage: record}}
    qcLog.forEach((r) => {
      if (r.status === "pending") return; // skip pending-inspection entries
      if (!map[r.job_id]) map[r.job_id] = {};
      if (!map[r.job_id][r.stage]) map[r.job_id][r.stage] = r;
    });
    return map;
  }, [qcLog]);

  // Phase 35.4 — active subcontract DC keyed by "jobId|stage".
  // Job Work DC (subcontract-outward) is available at every stage except
  // Finished Goods (a terminal stage — there's nothing further to send out).
  const SUBCONTRACT_STAGE_KEYS = STAGES.filter((s) => s.key !== "finished" && s.key !== "rework").map((s) => s.key);
  const activeScDcByJobStage = useMemo(() => {
    const map = {};
    subcontractDcs.forEach((d) => {
      map[`${d.job_id}|${d.stage}`] = d;
    });
    return map;
  }, [subcontractDcs]);

  // Every active, not-yet-sent job at the stage the "Send Outside" dialog was
  // opened for — the full pick-list the operator selects a subset from.
  const scJobs = useMemo(() => {
    if (!scStage) return [];
    return jobs.filter((j) =>
      j.current_stage === scStage &&
      j.status === "active" &&
      Number(j.current_qty || 0) > 0 &&
      !activeScDcByJobStage[`${j.id}|${scStage}`]
    );
  }, [jobs, scStage, activeScDcByJobStage]);

  // scSelection maps job_id -> { qty, output_item_id }. output_item_id is
  // what the item should be RECEIVED BACK as — job work can transform the
  // part number (e.g. a raw bar goes out, a machined part comes back).
  // Defaults to the job's current item; the operator can change it per row.
  const defaultOutputItemId = (job) => job.current_item_id || job.fg_item_id || job.raw_item_id || "";

  const openSendOutside = (job) => {
    setScStage(job.current_stage);
    // Pre-select the job the button was clicked on; the rest of the stage's
    // items are listed too so the operator can add more to the same DC run.
    setScSelection({ [job.id]: { qty: job.current_qty || 0, output_item_id: defaultOutputItemId(job) } });
    setScForm({ vendor_id: suppliers[0]?.id || "", vehicle_no: "", notes: "" });
    setSendOpen(true);
  };

  const toggleScJob = (job) => {
    setScSelection((sel) => {
      if (Object.prototype.hasOwnProperty.call(sel, job.id)) {
        const { [job.id]: _omit, ...rest } = sel;
        return rest;
      }
      return { ...sel, [job.id]: { qty: job.current_qty || 0, output_item_id: defaultOutputItemId(job) } };
    });
  };

  const setScJobQty = (jobId, qty) => setScSelection((sel) => ({ ...sel, [jobId]: { ...sel[jobId], qty } }));
  const setScJobOutputItem = (jobId, output_item_id) => setScSelection((sel) => ({ ...sel, [jobId]: { ...sel[jobId], output_item_id } }));

  const allScJobsSelected = scJobs.length > 0 && scJobs.every((j) => Object.prototype.hasOwnProperty.call(scSelection, j.id));
  const toggleScSelectAll = () => {
    if (allScJobsSelected) { setScSelection({}); return; }
    const next = {};
    scJobs.forEach((j) => { next[j.id] = { qty: j.current_qty || 0, output_item_id: defaultOutputItemId(j) }; });
    setScSelection(next);
  };

  // Page-level "Create Job Work DC" (single-stage view): every eligible item
  // at the stage is shown pre-selected — the operator only edits quantity
  // (and output part, if job work changes it), or unchecks a row to leave
  // it out of this DC run.
  const openCreateJobWorkDc = (stageKey) => {
    const eligible = jobs.filter((j) =>
      j.current_stage === stageKey &&
      j.status === "active" &&
      Number(j.current_qty || 0) > 0 &&
      !activeScDcByJobStage[`${j.id}|${stageKey}`]
    );
    setScStage(stageKey);
    const sel = {};
    eligible.forEach((j) => { sel[j.id] = { qty: j.current_qty || 0, output_item_id: defaultOutputItemId(j) }; });
    setScSelection(sel);
    setScForm({ vendor_id: suppliers[0]?.id || "", vehicle_no: "", notes: "" });
    setSendOpen(true);
  };

  const submitSendOutside = async () => {
    const lines = Object.entries(scSelection).map(([job_id, sel]) => ({ job_id, qty: Number(sel.qty), output_item_id: sel.output_item_id || undefined }));
    if (!scForm.vendor_id) return toast.error("Please select a vendor");
    if (lines.length === 0) return toast.error("Select at least one item to send outside");
    if (lines.some((l) => !(l.qty > 0))) return toast.error("Quantity must be greater than 0 for every selected item");
    setBusy(true);
    try {
      const vendor = suppliers.find((s) => s.id === scForm.vendor_id);
      const res = await api.post(`/production-jobs/send-outside-bulk`, {
        vendor_id: scForm.vendor_id,
        vendor_name: vendor?.name || "",
        lines,
        vehicle_no: scForm.vehicle_no,
        notes: scForm.notes,
      });
      const count = res.data?.count ?? lines.length;
      toast.success(`Sent ${count} item${count === 1 ? "" : "s"} outside — ${count} DC${count === 1 ? "" : "s"} generated`);
      setSendOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send-outside failed");
    } finally { setBusy(false); }
  };

  // ---- Raw Material page — RM Store items (no job exists yet) ----
  // Every RAW item currently in stock; "manual" (job-less) subcontract DCs
  // are matched by item_id since there's no job/stage to key off.
  const activeManualScDcByItem = useMemo(() => {
    const map = {};
    subcontractDcs.forEach((d) => {
      if (d.job_id) return;
      const itemId = d.lines?.[0]?.item_id;
      if (itemId) map[itemId] = d;
    });
    return map;
  }, [subcontractDcs]);

  const rmAllRows = useMemo(() => {
    return inventory
      .filter((r) => r.warehouse === "RM_STORE" && Number(r.qty || 0) > 0)
      .map((r) => ({ item_id: r.item_id, qty: Number(r.qty || 0), item: itemMap[r.item_id] || {} }))
      .filter((r) => r.item.id) // drop rows for items no longer in the catalog
      .sort((a, b) => (a.item.code || "").localeCompare(b.item.code || ""));
  }, [inventory, itemMap]);

  const rmRows = useMemo(() => {
    const q = (stageSearch.raw_material || "").trim().toLowerCase();
    if (!q) return rmAllRows;
    return rmAllRows.filter((r) =>
      (r.item.code || "").toLowerCase().includes(q) || (r.item.name || "").toLowerCase().includes(q)
    );
  }, [rmAllRows, stageSearch.raw_material]);

  // "Create Job Work DC" for Raw Material: no job/stage — raises a manual
  // subcontract DC (POST /delivery-challans/manual-subcontract) instead of
  // the job-linked send-outside-bulk endpoint. onlyItemId narrows the
  // pre-selection to one row (the per-row "Send Outside" button); omitted,
  // every eligible item is pre-selected (the header button).
  const [rawDcOpen, setRawDcOpen] = useState(false);
  const [rawDcSelection, setRawDcSelection] = useState({});
  const [rawDcForm, setRawDcForm] = useState({ vendor_id: "", vehicle_no: "", notes: "" });
  // Job Work DCs never check/require stock (goods can be sent outside for
  // processing regardless of what RM_STORE currently shows — the debit only
  // happens once the GIN is received back), so this list is every RAW
  // catalog item minus ones already sent (activeManualScDcByItem), not just
  // ones with positive RM_STORE stock like the main Raw Material board.
  const rawDcEligible = useMemo(() => {
    const stockByItem = {};
    inventory.filter((r) => r.warehouse === "RM_STORE").forEach((r) => { stockByItem[r.item_id] = Number(r.qty || 0); });
    return rawItems
      .filter((it) => !activeManualScDcByItem[it.id])
      .map((it) => ({ item_id: it.id, qty: stockByItem[it.id] || 0, item: it }))
      .sort((a, b) => (a.item.code || "").localeCompare(b.item.code || ""));
  }, [rawItems, inventory, activeManualScDcByItem]);
  // rawDcSelection maps item_id -> { qty, output_item_id } — same idea as
  // scSelection: what comes back from job work may be a different part
  // number than the raw material that went out.
  const openRawMaterialDc = (onlyItemId) => {
    // The header button still only pre-selects items actually in stock (the
    // common case, unchanged) — zero-stock items are listed too (see
    // rawDcEligible) but left unchecked so a real qty has to be typed in
    // deliberately rather than defaulting to a submit-blocking 0.
    const rows = onlyItemId ? rawDcEligible.filter((r) => r.item_id === onlyItemId) : rawDcEligible.filter((r) => r.qty > 0);
    const sel = {};
    rows.forEach((r) => { sel[r.item_id] = { qty: r.qty, output_item_id: r.item_id }; });
    setRawDcSelection(sel);
    setRawDcForm({ vendor_id: suppliers[0]?.id || "", vehicle_no: "", notes: "" });
    setRawDcOpen(true);
  };
  const toggleRawDcItem = (itemId, defaultQty) => {
    setRawDcSelection((sel) => {
      if (Object.prototype.hasOwnProperty.call(sel, itemId)) {
        const { [itemId]: _omit, ...rest } = sel;
        return rest;
      }
      return { ...sel, [itemId]: { qty: defaultQty, output_item_id: itemId } };
    });
  };
  const setRawDcItemQty = (itemId, qty) => setRawDcSelection((sel) => ({ ...sel, [itemId]: { ...sel[itemId], qty } }));
  const setRawDcOutputItem = (itemId, output_item_id) => setRawDcSelection((sel) => ({ ...sel, [itemId]: { ...sel[itemId], output_item_id } }));
  const submitRawMaterialDc = async () => {
    const lines = Object.entries(rawDcSelection).map(([item_id, sel]) => ({ item_id, qty: Number(sel.qty), output_item_id: sel.output_item_id || undefined }));
    if (!rawDcForm.vendor_id) return toast.error("Please select a vendor");
    if (lines.length === 0) return toast.error("Select at least one item to send outside");
    if (lines.some((l) => !(l.qty > 0))) return toast.error("Quantity must be greater than 0 for every selected item");
    setBusy(true);
    try {
      const vendor = suppliers.find((s) => s.id === rawDcForm.vendor_id);
      await api.post("/delivery-challans/manual-subcontract", {
        vendor_id: rawDcForm.vendor_id,
        vendor_name: vendor?.name || "",
        lines,
        vehicle_no: rawDcForm.vehicle_no,
        notes: rawDcForm.notes,
      });
      toast.success(`Sent ${lines.length} item${lines.length === 1 ? "" : "s"} outside — Job Work DC generated`);
      setRawDcOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send-outside failed");
    } finally { setBusy(false); }
  };

  // Raw Material row "Edit": corrects RM Store qty directly via the
  // existing generic /inventory/move endpoint (delta-based, logged to the
  // Stock Ledger, refuses to go negative) — same idea as adjust-qty for jobs.
  const [rawEditOpen, setRawEditOpen] = useState(false);
  const [rawEditTarget, setRawEditTarget] = useState(null);
  const [rawEditForm, setRawEditForm] = useState({ new_qty: "", reason: "" });
  const openRawEdit = (row) => {
    setRawEditTarget(row);
    setRawEditForm({ new_qty: row.qty, reason: "" });
    setRawEditOpen(true);
  };
  const submitRawEdit = async () => {
    if (!rawEditTarget) return;
    const qty = Number(rawEditForm.new_qty);
    if (rawEditForm.new_qty === "" || Number.isNaN(qty) || qty < 0) return toast.error("Enter a valid quantity");
    const delta = qty - rawEditTarget.qty;
    if (Math.abs(delta) < 1e-9) { setRawEditOpen(false); return; }
    setBusy(true);
    try {
      await api.post("/inventory/move", {
        item_id: rawEditTarget.item_id,
        warehouse: "RM_STORE",
        delta,
        reason: `RM Store quantity corrected: ${rawEditTarget.qty} → ${qty}${rawEditForm.reason ? ` — ${rawEditForm.reason}` : ""}`,
      });
      toast.success(`${rawEditTarget.item.code} RM Store quantity updated to ${qty}`);
      setRawEditOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Quantity update failed");
    } finally { setBusy(false); }
  };

  const openReceiveGoods = (dc) => {
    setScDc(dc);
    // One received qty per DC line with qty still remaining, pre-filled with
    // that remaining qty — a line left at 0 stays pending (the DC stays
    // open) for a later GIN.
    setScReceiveForm({
      received_qtys: (dc.lines || []).map((l) => (isDcLineReceived(dc, l) ? 0 : dcLinePendingQty(l))),
      // Received Part Number (stage after job work) per line — required.
      received_item_ids: (dc.lines || []).map(() => ""),
      // Target Store override per line — blank = the received part's stage store.
      target_stores: (dc.lines || []).map(() => ""),
      notes: "",
    });
    setReceiveOpen(true);
  };

  const submitReceiveGoods = async () => {
    const lines = scReceiveForm.received_qtys
      .map((q, idx) => ({ dc_line_idx: idx, received_qty: Number(q || 0), received_item_id: scReceiveForm.received_item_ids[idx] || null, target_store: scReceiveForm.target_stores?.[idx] || "" }))
      .filter((l) => !isDcLineReceived(scDc, scDc.lines[l.dc_line_idx]));
    if (lines.some((l) => l.received_qty < 0)) return toast.error("Received quantity cannot be negative");
    if (!lines.some((l) => l.received_qty > 0)) return toast.error("Received quantity must be greater than 0");
    const over = lines.find((l) => l.received_qty > dcLinePendingQty(scDc.lines[l.dc_line_idx]) + 1e-6);
    if (over) return toast.error(`DC line ${over.dc_line_idx + 1}: received qty exceeds the remaining qty ${fmtQty(dcLinePendingQty(scDc.lines[over.dc_line_idx]))}`);
    const noPart = lines.find((l) => l.received_qty > 0 && !l.received_item_id && !(scDc.lines[l.dc_line_idx].is_manual && !scDc.lines[l.dc_line_idx].item_id));
    if (noPart) return toast.error(`DC line ${noPart.dc_line_idx + 1}: select the Received Part Number (stage after job work)`);
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-challans/${scDc.id}/receive-goods`, {
        lines,
        notes: scReceiveForm.notes,
      });
      toast.success(data.dc.status === "delivered"
        ? `Goods received — GIN ${data.gin.code} generated, DC ${scDc.code} closed`
        : `Goods received — GIN ${data.gin.code} generated, DC ${scDc.code} stays open for the remaining qty`);
      setReceiveOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Receive failed");
    } finally { setBusy(false); }
  };

  // Raw Material page's per-row "Issue" pre-fills the raw item; the header
  // "Issue Material to Machine Shop" button uses this with no argument.
  // fg_item_id starts blank rather than guessing — MRP Run now resolves a
  // batch's Finished Good automatically from the issued/output item's own
  // `parent_fg_item_id` (set once in Items & BOM), so this field is just an
  // optional override, not something every operator has to fill in.
  const startIssue = (rawItemId) => {
    setIssueForm({
      raw_item_id: rawItemId || rawItems[0]?.id || "",
      fg_item_id: "",
      output_item_id: "",
      raw_qty: 0, batch_no: "", operator: "", machine: "",
      expected_completion: "",
    });
    setIssueOpen(true);
  };

  const submitIssue = async () => {
    if (!issueForm.raw_item_id || issueForm.raw_qty <= 0) return toast.error("Pick raw material and qty");
    setBusy(true);
    try {
      const payload = {
        ...issueForm,
        fg_item_id: issueForm.fg_item_id || null,
        output_item_id: issueForm.output_item_id || null,
        expected_completion: issueForm.expected_completion ? new Date(issueForm.expected_completion).toISOString() : null,
      };
      await api.post("/production-jobs", payload);
      toast.success("Job created — material issued to Machine Shop");
      setIssueOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Issue failed");
    } finally { setBusy(false); }
  };

  // ---- Phase 28/33: Issue dialog — collects output part, destination stage (forward, may skip),
  //                    destination store, moved qty, and (if skipping) an optional skip reason.
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [qcTarget, setQcTarget] = useState(null);
  const [qcForm, setQcForm] = useState({ source_item_id: "", output_item_id: "", dest_stage: "", dest_store: "", moved_qty: "", skip_reason: "" });

  // `pickJobs` is set when opened from a stage page's header "Issue" (no
  // job chosen yet): the dialog then offers a Job picker over those jobs.
  const openSendForQc = (job, pickJobs = null) => {
    const routing = routingForJob(job);
    const forwardStages = job.current_stage === "rework"
      ? (job.rework_from_stage ? [STAGES.find((s) => s.key === job.rework_from_stage)].filter(Boolean) : [])
      : routingForwardStages(routing, job.current_stage);
    if (forwardStages.length === 0) return toast.error("Job has no forward stage");
    const preferredNext = DEFAULT_NEXT_STAGE[job.current_stage];
    const nextStage = forwardStages.some((s) => s.key === preferredNext) ? preferredNext : forwardStages[0].key;
    const defaultStore = STAGE_STORE[nextStage] || "";
    const expectedType = STAGE_ITEM_TYPE[nextStage];
    const curItem = itemMap[job.current_item_id];
    // Routing's own part number for this stage wins as the default — it's
    // authoritative for what this FG's output is at each stage — falling
    // back to carrying the current item forward when it already matches.
    const routingOutput = routing?.stage_outputs?.[nextStage];
    const defaultOutput = routingOutput || (curItem && curItem.type === expectedType ? job.current_item_id : "");
    setQcTarget({ ...job, _forward_stages: forwardStages, _next_stage: nextStage, _default_store: defaultStore, _routing: routing, _pick_jobs: pickJobs });
    const curPart = job.current_item_id || job.raw_item_id || "";
    setQcForm({ source_item_id: curPart, source_pick: curPart ? `stage|${curPart}` : "", output_item_id: defaultOutput, dest_stage: nextStage, dest_store: defaultStore, moved_qty: "", skip_reason: "" });
    setQcDialogOpen(true);
  };

  // Stage page header "Issue": the same per-job Issue dialog as each row's
  // Issue button, starting on the first job at this stage that has qty
  // left to issue (not awaiting Receive, not sent outside).
  const openStageIssue = (stageKey) => {
    const issuable = stageJobsForView(stageKey).filter((j) =>
      j.status === "active" && Number(j.current_qty || 0) > 0 &&
      !(j.parent_job_id && !j.received_at && stageKey !== "rework"));
    if (issuable.length > 0) return openSendForQc(issuable[0], issuable);
    // No open job here (e.g. after the go-live clean-up closed them all) —
    // issue straight from whatever is in stock at this stage's store.
    const store = STAGE_STORE[stageKey];
    const hasStock = inventory.some((r) => r.warehouse === store && Number(r.qty || 0) > 0);
    if (!hasStock) return toast.error(`No job or stock at ${STAGE_LABEL[stageKey]} to issue — ${store} is empty`);
    openStoreStockIssue(stageKey);
  };

  const qcRoutingOutput = itemMap[qcTarget?._routing?.stage_outputs?.[qcForm.dest_stage]] || null;

  // Current Part Number choices: parts of the active jobs at this stage, and
  // parts in stock at this stage's store. Option values are "stage|<id>" /
  // "stock|<id>" so a part listed in both groups stays distinct.
  const qcCurrentPartOptions = useMemo(() => {
    if (!qcTarget) return { stage: [], stock: [], store: "" };
    const stageKey = qcTarget.current_stage;
    const store = STAGE_STORE[stageKey] || "";
    const stageIds = [qcTarget.current_item_id || qcTarget.raw_item_id];
    jobs.forEach((j) => {
      if (j.current_stage === stageKey && j.status === "active") stageIds.push(j.current_item_id || j.raw_item_id);
    });
    const stockQty = {};
    inventory.forEach((r) => {
      if (r.warehouse === store && Number(r.qty || 0) > 0) stockQty[r.item_id] = (stockQty[r.item_id] || 0) + Number(r.qty);
    });
    const stage = [...new Set(stageIds.filter(Boolean))].map((id) => itemMap[id]).filter(Boolean);
    const stock = Object.keys(stockQty).map((id) => itemMap[id] && { ...itemMap[id], _qty: stockQty[id] }).filter(Boolean)
      .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    return { stage, stock, store };
  }, [qcTarget, jobs, inventory, itemMap]);

  // Recompute defaults when the operator changes the destination stage in the dialog.
  const onChangeDestStage = (newStage) => {
    if (!qcTarget) return;
    const defaultStore = STAGE_STORE[newStage] || "";
    const expectedType = STAGE_ITEM_TYPE[newStage];
    const curItem = itemMap[qcTarget.current_item_id];
    const routingOutput = qcTarget._routing?.stage_outputs?.[newStage];
    const nextOutput = routingOutput || (curItem && curItem.type === expectedType ? qcTarget.current_item_id : "");
    setQcForm((f) => ({ ...f, dest_stage: newStage, dest_store: defaultStore, output_item_id: nextOutput }));
  };

  const submitSendForQc = async () => {
    if (!qcTarget) return;
    if (!qcForm.dest_stage) return toast.error("Select the destination stage");
    if (!qcForm.output_item_id) return toast.error("Select the output part number for this stage");
    if (!qcForm.dest_store) return toast.error("Select the destination store");
    const moved = Number(qcForm.moved_qty);
    if (!qcForm.moved_qty || moved <= 0) return toast.error("Enter the quantity to move to the next stage");
    if (moved > (qcTarget.current_qty || 0) + 1e-6) return toast.error(`Quantity to move (${moved}) exceeds remaining (${qcTarget.current_qty}) at this stage`);
    // If skipping stages, require a reason — unless every skipped stage is
    // simply blank in this FG's Routing (never meant to exist in the flow,
    // so it isn't really a "skip" an operator needs to justify).
    const curIdx = STAGES.findIndex((s) => s.key === qcTarget.current_stage);
    const destIdx = STAGES.findIndex((s) => s.key === qcForm.dest_stage);
    const isSkipping = destIdx > curIdx + 1 && !isDefaultNext(qcTarget.current_stage, qcForm.dest_stage);
    const routing = qcTarget._routing;
    const skippedKeys = isSkipping ? STAGES.slice(curIdx + 1, destIdx).map((s) => s.key) : [];
    const needsReason = isRoutingConfigured(routing)
      ? skippedKeys.some((k) => routing.stage_outputs?.[k])
      : isSkipping;
    if (needsReason && !(qcForm.skip_reason || "").trim()) return toast.error("Enter a reason when skipping intermediate stages");
    setBusy(true);
    try {
      await api.post(`/production-jobs/${qcTarget.id}/request-qc`, {
        output_item_id: qcForm.output_item_id,
        dest_store: qcForm.dest_store,
        moved_qty: moved,
        next_stage_override: qcForm.dest_stage,
        skip_reason: qcForm.skip_reason || "",
        source_item_id: qcForm.source_item_id || null,
      });
      const skipNote = isSkipping ? ` (skipped ${destIdx - curIdx - 1} stage${destIdx - curIdx - 1 === 1 ? "" : "s"})` : "";
      toast.success(`Batch of ${moved} from ${qcTarget.code} issued to ${STAGE_LABEL[qcForm.dest_stage]}${skipNote}`);
      setQcDialogOpen(false);
      setQcTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Issue failed");
    } finally { setBusy(false); }
  };

  // ---- Edit a still-PENDING partial batch (a request-qc call awaiting an
  // inspector's decision) — quantity, output part number, destination stage
  // (may still skip forward), or destination store, all correctable before
  // it's completed. A job can have more than one pending batch at once, so
  // the pencil on the "Awaiting QC" chip opens a small picker first.
  const [pendingListOpen, setPendingListOpen] = useState(false);
  const [pendingListJob, setPendingListJob] = useState(null);
  const openPendingList = (job) => { setPendingListJob(job); setPendingListOpen(true); };

  const [editInspOpen, setEditInspOpen] = useState(false);
  const [editInspTarget, setEditInspTarget] = useState(null);
  const [editInspForm, setEditInspForm] = useState({ output_item_id: "", dest_stage: "", dest_store: "", moved_qty: "", skip_reason: "" });

  const openEditPendingBatch = (insp, job) => {
    const routing = job ? routingForJob(job) : null;
    const forwardStages = insp.stage === "rework"
      ? (job?.rework_from_stage ? [STAGES.find((s) => s.key === job.rework_from_stage)].filter(Boolean) : [])
      : routingForwardStages(routing, insp.stage);
    setEditInspTarget({ ...insp, _job: job, _forward_stages: forwardStages, _routing: routing });
    setEditInspForm({
      output_item_id: insp.output_item_id || "",
      dest_stage: insp.next_stage || forwardStages[0]?.key || "",
      dest_store: insp.dest_store || STAGE_STORE[insp.next_stage] || "",
      moved_qty: insp.pending_qty,
      skip_reason: insp.skip_reason || "",
    });
    setPendingListOpen(false);
    setEditInspOpen(true);
  };

  const onChangeEditDestStage = (newStage) => {
    if (!editInspTarget) return;
    const defaultStore = STAGE_STORE[newStage] || "";
    const expectedType = STAGE_ITEM_TYPE[newStage];
    const curOutputItem = itemMap[editInspForm.output_item_id];
    const routingOutput = editInspTarget._routing?.stage_outputs?.[newStage];
    const nextOutput = routingOutput || (curOutputItem && curOutputItem.type === expectedType ? editInspForm.output_item_id : "");
    setEditInspForm((f) => ({ ...f, dest_stage: newStage, dest_store: defaultStore, output_item_id: nextOutput }));
  };

  const submitEditPendingBatch = async () => {
    if (!editInspTarget) return;
    if (!editInspForm.dest_stage) return toast.error("Select the destination stage");
    if (!editInspForm.output_item_id) return toast.error("Select the output part number for this stage");
    if (!editInspForm.dest_store) return toast.error("Select the destination store");
    const moved = Number(editInspForm.moved_qty);
    const job = editInspTarget._job;
    const available = Number(job?.current_qty || 0) + Number(editInspTarget.pending_qty || 0);
    if (!editInspForm.moved_qty || moved <= 0) return toast.error("Enter the quantity to move");
    if (moved > available + 1e-6) return toast.error(`Quantity to move (${moved}) exceeds available (${available})`);
    const curIdx = STAGES.findIndex((s) => s.key === editInspTarget.stage);
    const destIdx = STAGES.findIndex((s) => s.key === editInspForm.dest_stage);
    const isSkipping = destIdx > curIdx + 1 && !isDefaultNext(editInspTarget.stage, editInspForm.dest_stage);
    const editRouting = editInspTarget._routing;
    const editSkippedKeys = isSkipping ? STAGES.slice(curIdx + 1, destIdx).map((s) => s.key) : [];
    const editNeedsReason = isRoutingConfigured(editRouting)
      ? editSkippedKeys.some((k) => editRouting.stage_outputs?.[k])
      : isSkipping;
    if (editNeedsReason && !(editInspForm.skip_reason || "").trim()) return toast.error("Enter a reason when skipping intermediate stages");
    setBusy(true);
    try {
      await api.put(`/quality/inspections/${editInspTarget.id}/edit`, {
        output_item_id: editInspForm.output_item_id,
        dest_store: editInspForm.dest_store,
        moved_qty: moved,
        next_stage_override: editInspForm.dest_stage,
        skip_reason: editInspForm.skip_reason || "",
      });
      toast.success(`Pending batch updated — ${moved} → ${STAGE_LABEL[editInspForm.dest_stage]}`);
      setEditInspOpen(false);
      setEditInspTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  // Phase 33 — Receive: destination-stage operator acknowledges arrival of a pass-forward child job.
  const receiveJob = async (job) => {
    setBusy(true);
    try {
      await api.post(`/production-jobs/${job.id}/receive`, { notes: "" });
      toast.success(`${job.code} received at ${STAGE_LABEL[job.current_stage]}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Receive failed");
    } finally { setBusy(false); }
  };

  // Single-stage page — "Receive" button lists every pending-arrival job at
  // this stage in one dialog instead of hunting for each card individually.
  const [receiveStageOpen, setReceiveStageOpen] = useState(false);
  const [receiveStageKey, setReceiveStageKey] = useState(null);
  const receiveStageJobs = useMemo(() => {
    if (!receiveStageKey) return [];
    return jobs.filter((j) =>
      j.current_stage === receiveStageKey &&
      !!j.parent_job_id && !j.received_at && j.status === "active" &&
      receiveStageKey !== "rework" && receiveStageKey !== "finished"
    );
  }, [jobs, receiveStageKey]);
  const openReceiveForStage = (stageKey) => {
    setReceiveStageKey(stageKey);
    setReceiveStageOpen(true);
  };

  // Phase 33 — per-stage real-time search (state declared near the top of
  // the component — see comment there)
  const setStageQuery = (stageKey, value) => setStageSearch((s) => ({ ...s, [stageKey]: value }));
  const stageQueryMatches = (job, stageKey) => {
    const q = (stageSearch[stageKey] || "").trim().toLowerCase();
    if (!q) return true;
    const item = itemMap[job.current_item_id] || itemMap[job.fg_item_id] || itemMap[job.raw_item_id] || {};
    return (
      (item.code || "").toLowerCase().includes(q) ||
      (item.name || "").toLowerCase().includes(q) ||
      (job.code || "").toLowerCase().includes(q) ||
      (job.batch_no || "").toLowerCase().includes(q)
    );
  };

  // Status + started-on date range check for the single-stage filter bar.
  const stageFilterMatches = (job, stageKey) => {
    const f = getStageFilter(stageKey);
    if (f.status === "active" && job.status !== "active") return false;
    if (f.status === "closed" && job.status === "active") return false;
    if (f.from || f.to) {
      const startedAt = job.started_at ? new Date(job.started_at) : null;
      if (!startedAt) return false;
      if (f.from && startedAt < new Date(f.from)) return false;
      if (f.to) {
        const to = new Date(f.to);
        to.setHours(23, 59, 59, 999);
        if (startedAt > to) return false;
      }
    }
    return true;
  };

  // Every job currently sitting at a single-stage page's stage — regardless
  // of active/closed status — minus ones out on an open Job Work DC (those
  // render in the separate "Sent Outside" section). This is the superset the
  // filter bar above narrows down; unlike the all-stages Kanban's `cols`
  // (a live-WIP view that already has its own "Recently Closed Jobs" strip),
  // a single stage's own page is the one place a closed/cancelled job that
  // ended at that stage is visible at all, so it must not be status-filtered
  // by default.
  const stageJobsForView = (stageKey) =>
    jobs.filter((j) => j.current_stage === stageKey && !activeScDcByJobStage[`${j.id}|${stageKey}`]);

  // Phase 33.1 — Issue-to-Stage (direct issue, independent of default flow)
  const [directOpen, setDirectOpen] = useState(false);
  // When opened from a single-stage page's "Issue" button, the destination
  // stage is fixed to that page and the selector is locked read-only.
  const [directLocked, setDirectLocked] = useState(false);
  const [directLockedStage, setDirectLockedStage] = useState("");
  const [workOrders, setWorkOrders] = useState([]);
  const [directForm, setDirectForm] = useState({ item_id: "", qty: "", source_store: "", dest_stage: "", work_order_id: "", notes: "" });
  const [directItemQuery, setDirectItemQuery] = useState("");
  // Raw Material page's header "Issue": same dialog, but the part list is the
  // source store's stock, the destination is editable to any stage, and only
  // open Work Orders are offered.
  const [directFromRm, setDirectFromRm] = useState(false);
  const openDirectIssue = (lockedStageKey) => {
    setDirectFromRm(false);
    setDirectForm({ item_id: "", qty: "", source_store: STAGE_STORE[lockedStageKey] || "", dest_stage: nextFlowStage(lockedStageKey), work_order_id: "", notes: "" });
    setDirectItemQuery("");
    setDirectLocked(!!lockedStageKey);
    setDirectLockedStage(lockedStageKey || "");
    setDirectOpen(true);
  };
  // Stage page "Issue" with no open job at the stage: same stock-driven
  // dialog as Raw Material's, sourced from this stage's own store.
  const openStoreStockIssue = (stageKey) => {
    setDirectFromRm(true);
    setDirectForm({ item_id: "", qty: "", source_store: STAGE_STORE[stageKey] || "", dest_stage: nextFlowStage(stageKey), work_order_id: "", notes: "" });
    setDirectItemQuery("");
    setDirectLocked(false);
    setDirectLockedStage(stageKey);
    setDirectOpen(true);
  };
  const openRawMaterialDirectIssue = () => {
    setDirectFromRm(true);
    setDirectForm({ item_id: "", qty: "", source_store: RAW_MATERIAL_STAGE.store, dest_stage: nextFlowStage(RAW_MATERIAL_STAGE.key), work_order_id: "", notes: "" });
    setDirectItemQuery("");
    setDirectLocked(false);
    setDirectLockedStage(RAW_MATERIAL_STAGE.key);
    setDirectOpen(true);
  };
  const directStockQty = useMemo(() => {
    const m = {};
    inventory.forEach((r) => { if (r.warehouse === directForm.source_store) m[r.item_id] = (m[r.item_id] || 0) + Number(r.qty || 0); });
    return m;
  }, [inventory, directForm.source_store]);
  const directItemMatches = useMemo(() => {
    const q = directItemQuery.trim().toLowerCase();
    let pool = items;
    if (directFromRm) {
      const inStock = new Set(inventory
        .filter((r) => r.warehouse === directForm.source_store && Number(r.qty || 0) > 0)
        .map((r) => r.item_id));
      pool = items.filter((i) => inStock.has(i.id));
    }
    if (!q) return pool.slice(0, 50);
    return pool.filter((i) =>
      (i.code || "").toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q)
    ).slice(0, 60);
  }, [items, directItemQuery, directFromRm, inventory, directForm.source_store]);
  const OPEN_WO_STATUSES = ["planned", "released", "in_progress"];
  const directWorkOrders = directFromRm
    ? workOrders.filter((w) => !w.status || OPEN_WO_STATUSES.includes(w.status))
    : workOrders;

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/work-orders"); setWorkOrders(r.data || []); } catch { setWorkOrders([]); }
    })();
  }, []);

  // Phase 35.5 — live available-stock probe for the Issue-to-Stage form.
  const [directAvailable, setDirectAvailable] = useState(null);
  useEffect(() => {
    if (!directForm.item_id || !directForm.source_store) { setDirectAvailable(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/inventory/available?item_id=${directForm.item_id}&warehouse=${directForm.source_store}`);
        if (!cancelled) setDirectAvailable(Number(data.available || 0));
      } catch { if (!cancelled) setDirectAvailable(null); }
    })();
    return () => { cancelled = true; };
  }, [directForm.item_id, directForm.source_store, directOpen]);

  const submitDirectIssue = async () => {
    if (!directForm.item_id) return toast.error("Select the part");
    const qty = Number(directForm.qty);
    if (!qty || qty <= 0) return toast.error("Quantity must be > 0");
    if (!directForm.source_store) return toast.error("Select the source store");
    if (!directForm.dest_stage) return toast.error("Select the destination stage");
    if (directAvailable !== null && qty > directAvailable + 1e-6) {
      return toast.error(`Only ${directAvailable} available at source — cannot issue ${qty}`);
    }
    setBusy(true);
    try {
      const { data } = await api.post("/production-jobs/direct-issue", {
        item_id: directForm.item_id,
        qty,
        source_store: directForm.source_store,
        dest_stage: directForm.dest_stage,
        work_order_id: directForm.work_order_id || null,
        notes: directForm.notes || "",
      });
      const jobCode = data.job?.code;
      toast.success(`${jobCode} moved to ${STAGE_LABEL[directForm.dest_stage]}`);
      setDirectOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Direct issue failed");
    } finally { setBusy(false); }
  };

  // ---- Phase 28/30: Assembly Materials Issue ----
  const [asmDialogOpen, setAsmDialogOpen] = useState(false);
  const [asmTarget, setAsmTarget] = useState(null);
  const [asmPreview, setAsmPreview] = useState(null);
  const [asmLines, setAsmLines] = useState([]);
  const [asmRemarks, setAsmRemarks] = useState("");
  const [asmShortages, setAsmShortages] = useState({});  // Phase 30 — {component_code: {required, available, short_by}}

  const openIssueMaterials = async (job) => {
    setAsmTarget(job);
    setAsmDialogOpen(true);
    setAsmPreview(null);
    setAsmLines([]);
    setAsmRemarks("");
    setAsmShortages({});
    try {
      const { data } = await api.get(`/production-jobs/${job.id}/assembly-materials`);
      setAsmPreview(data);
      setAsmLines(data.components.map((c) => ({ component_item_id: c.component_item_id, qty: c.default_qty })));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load BOM");
      setAsmDialogOpen(false);
    }
  };

  const submitIssueMaterials = async () => {
    if (!asmTarget || !asmPreview) return;
    const lines = asmLines.filter((l) => Number(l.qty) > 0);
    if (lines.length === 0) return toast.error("Enter at least one component quantity");
    setAsmShortages({});
    setBusy(true);
    try {
      await api.post(`/production-jobs/${asmTarget.id}/issue-assembly-materials`, {
        lines: lines.map((l) => ({ component_item_id: l.component_item_id, qty: Number(l.qty) })),
        remarks: asmRemarks,
      });
      toast.success(`Materials issued for ${asmTarget.code} · marked Materials Issued`);
      setAsmDialogOpen(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.shortages)) {
        // Phase 30 — highlight each shortage row and toast the aggregated message
        const map = {};
        detail.shortages.forEach((s) => { map[s.component_code] = s; });
        setAsmShortages(map);
        toast.error(detail.message || "Insufficient SFG_STORE stock");
      } else {
        toast.error(formatApiError(detail) || "Assembly issue failed");
      }
    } finally { setBusy(false); }
  };

  const cancelJob = async (job) => {
    if (!window.confirm(`Cancel ${job.code}? Remaining WIP returns to RM Store.`)) return;
    try {
      await api.delete(`/production-jobs/${job.id}`);
      toast.success("Job cancelled");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Single-stage list — "Edit" only ever touches the Remaining quantity at
  // this stage (a stock-accurate correction, e.g. a miscount); nothing else
  // about the job is editable here.
  const [adjustQtyOpen, setAdjustQtyOpen] = useState(false);
  const [adjustQtyTarget, setAdjustQtyTarget] = useState(null);
  const [adjustQtyForm, setAdjustQtyForm] = useState({ new_qty: "", reason: "" });
  const openAdjustQty = (job) => {
    setAdjustQtyTarget(job);
    setAdjustQtyForm({ new_qty: job.current_qty ?? 0, reason: "" });
    setAdjustQtyOpen(true);
  };
  const submitAdjustQty = async () => {
    if (!adjustQtyTarget) return;
    const qty = Number(adjustQtyForm.new_qty);
    if (adjustQtyForm.new_qty === "" || Number.isNaN(qty) || qty < 0) return toast.error("Enter a valid quantity");
    setBusy(true);
    try {
      await api.post(`/production-jobs/${adjustQtyTarget.id}/adjust-qty`, {
        new_qty: qty,
        reason: adjustQtyForm.reason || "",
      });
      toast.success(`${adjustQtyTarget.code} remaining quantity updated to ${qty}`);
      setAdjustQtyOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Quantity update failed");
    } finally { setBusy(false); }
  };

  // A job with an open (dispatched) Job Work DC at its current stage has
  // physically left that stage's store — it must not show up (or count) in
  // the main store list until Goods Received (GIN) brings it back. Those
  // jobs are pulled out here and shown separately via sentOutsideCols below.
  const cols = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    jobs.forEach((j) => {
      const includeClosed = j.status !== "active" && (j.current_stage === "rework" || j.current_stage === "finished");
      if (!((j.status === "active" || includeClosed) && out[j.current_stage])) return;
      if (activeScDcByJobStage[`${j.id}|${j.current_stage}`]) return;
      out[j.current_stage].push(j);
    });
    return out;
  }, [jobs, activeScDcByJobStage]);

  // Jobs currently sent outside for job work — an open Job Work DC exists at
  // their current stage. Rendered as a distinct "Sent Outside" section on
  // both the single-stage and Kanban views, with the Goods Received action
  // that closes the DC (raises the GIN) and moves the job back into `cols`.
  const sentOutsideCols = useMemo(() => {
    const out = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    jobs.forEach((j) => {
      if (j.status !== "active" || !out[j.current_stage]) return;
      if (activeScDcByJobStage[`${j.id}|${j.current_stage}`]) out[j.current_stage].push(j);
    });
    return out;
  }, [jobs, activeScDcByJobStage]);

  // QC status indicator per card
  const qcIndicator = (j) => {
    if (j.status === "cancelled") {
      return { label: "Cancelled", tone: "bg-slate-100 text-slate-500 border-slate-300", icon: XCircle };
    }
    if (j.status === "closed" && j.current_stage !== "finished") {
      return { label: "Closed", tone: "bg-slate-100 text-slate-500 border-slate-300", icon: CheckCircle2 };
    }
    // A batch at Finished Goods has already been credited to the FG store —
    // whether it arrived via Final Inspection QC, a GIN or a Direct Issue.
    if (j.current_stage === "finished") {
      return { label: "Credited to FG", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 };
    }
    if (j.qc_status === "awaiting_qc") {
      return { label: "Awaiting QC", tone: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock };
    }
    if (j.qc_status === "rework") {
      return { label: "QC Failed", tone: "bg-red-100 text-red-800 border-red-300", icon: XCircle };
    }
    const stageQc = latestCompletedQc[j.id]?.[j.current_stage];
    if (stageQc?.decision === "pass" || stageQc?.decision === "approved") {
      return { label: "QC Passed", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 };
    }
    if (stageQc?.decision === "partial") {
      return { label: "QC Partial", tone: "bg-blue-100 text-blue-800 border-blue-300", icon: CheckCircle2 };
    }
    return { label: "In Process", tone: "bg-slate-100 text-slate-700 border-slate-300", icon: ShieldCheck };
  };

  // Shared job-card renderer — used by both the all-stages Kanban column and
  // the single-stage page's job list, so the two views never drift apart.
  const renderJobCard = (j, stage) => {
    const item = itemMap[j.current_item_id] || itemMap[j.fg_item_id] || itemMap[j.raw_item_id] || {};
    const isTerminalFinished = stage.key === "finished";
    const ind = qcIndicator(j);
    const IndIcon = ind.icon;
    // Phase 29 — partial movement quantities
    const total = Number(j.stage_input_qty ?? j.current_qty ?? 0);
    const inflight = inflightByJob[j.id] || 0;
    const remaining = Number(j.current_qty ?? 0);
    const moved = Math.max(0, total - remaining - inflight);
    const hasInflight = inflight > 0;
    // Phase 33 — pending Receive: child job spawned from an upstream QC-pass
    // that the destination-stage operator hasn't yet acknowledged.
    const pendingReceive = !!j.parent_job_id && !j.received_at && j.status === "active" && stage.key !== "rework" && stage.key !== "finished";

    return (
      <div key={j.id} className={`bg-white rounded-md p-3 border hover:shadow-sm transition ${pendingReceive ? "border-cyan-400 ring-1 ring-cyan-200" : hasInflight ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`} data-testid={`job-card-${j.code}`}>
        <div className="flex justify-between items-start">
          <div className="mono text-[10px] uppercase tracking-wider text-slate-500">
            {j.code}
            {j.parent_job_id && <span className="ml-1 text-purple-600" title="Child batch">↳</span>}
          </div>
          <span className="text-[10px] mono px-1.5 py-0.5 bg-slate-100 rounded">{remaining} {item.uom || ""}</span>
        </div>
        <div className="mt-1 text-sm font-medium text-slate-800 truncate">{item.name || "—"}</div>
        {j.current_item_id && itemMap[j.current_item_id] && j.current_item_id !== j.raw_item_id && (
          <div className="text-[10px] text-blue-700 mono truncate">Part: {itemMap[j.current_item_id].code}</div>
        )}
        <div className="mt-0.5 text-[11px] text-slate-500 truncate">
          <Hash className="inline w-3 h-3 mr-0.5" /> {j.batch_no || "—"}
          {j.operator && <> · <UserIcon className="inline w-3 h-3 mr-0.5" />{j.operator}</>}
        </div>

        {/* Phase 29 — Moved / Remaining split */}
        <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] mono" data-testid={`qty-split-${j.code}`}>
          <div className="rounded bg-slate-50 border border-slate-200 px-1.5 py-0.5">
            <div className="uppercase text-[9px] text-slate-500 tracking-wide">Total</div>
            <div className="text-slate-800 font-semibold" data-testid={`qty-total-${j.code}`}>{total}</div>
          </div>
          <div className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
            <div className="uppercase text-[9px] text-emerald-700 tracking-wide">Moved</div>
            <div className="text-emerald-800 font-semibold" data-testid={`qty-moved-${j.code}`}>{moved}</div>
          </div>
          <div className="rounded bg-blue-50 border border-blue-200 px-1.5 py-0.5">
            <div className="uppercase text-[9px] text-blue-700 tracking-wide">Remaining</div>
            <div className="text-blue-800 font-semibold" data-testid={`qty-remaining-${j.code}`}>{remaining}</div>
          </div>
        </div>
        {hasInflight && (
          <button type="button" className="mt-1 text-[10px] text-amber-700 flex items-center gap-1 hover:underline" onClick={() => openPendingList(j)} data-testid={`inflight-chip-${j.code}`} title="Edit pending batch(es)">
            <Clock className="w-3 h-3" /> Awaiting QC: {inflight} {item.uom || ""} <Pencil className="w-2.5 h-2.5" />
          </button>
        )}

        {/* Status badges + actions — one horizontal row (wraps rather than
            breaking the card width if it runs out of room). */}
        <div className="mt-2 flex items-center flex-wrap gap-2" data-testid={`actions-row-${j.code}`}>
          {/* QC status indicator */}
          <div className={`text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold flex items-center gap-1 border ${ind.tone}`} data-testid={`qc-indicator-${j.code}`}>
            <IndIcon className="w-3 h-3" /> {ind.label}
          </div>

          {/* Phase 35.4 — Subcontract status chip (Sent Outside) */}
          {SUBCONTRACT_STAGE_KEYS.includes(stage.key) && activeScDcByJobStage[`${j.id}|${stage.key}`] && (
            <div className="text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold flex items-center gap-1 border bg-orange-100 text-orange-800 border-orange-300" data-testid={`sc-sent-chip-${j.code}`}>
              <Send className="w-3 h-3" /> Sent Outside · {activeScDcByJobStage[`${j.id}|${stage.key}`].code}
            </div>
          )}

          {/* Phase 30 — Materials Issued chip (Assembly stage only) */}
          {j.current_stage === "assembly" && j.materials_issued && (
            <div className="text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold flex items-center gap-1 border bg-indigo-100 text-indigo-800 border-indigo-300" data-testid={`materials-issued-chip-${j.code}`}>
              <Package className="w-3 h-3" /> Materials Issued
            </div>
          )}

          {/* Phase 33 — Pending Receive chip */}
          {pendingReceive && (
            <div className="text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold flex items-center gap-1 border bg-cyan-100 text-cyan-800 border-cyan-300" data-testid={`pending-receive-chip-${j.code}`}>
              <Package className="w-3 h-3" /> Pending Receive
            </div>
          )}

          {/* Actions */}
          {!isTerminalFinished && j.status === "active" && (
            <>
              {/* Phase 35.4 — Subcontract flow: Send Outside / Goods Received */}
              {SUBCONTRACT_STAGE_KEYS.includes(stage.key) && (
                activeScDcByJobStage[`${j.id}|${stage.key}`] ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-50" onClick={() => openReceiveGoods(activeScDcByJobStage[`${j.id}|${stage.key}`])} data-testid={`sc-receive-btn-${j.code}`} disabled={busy}>
                    <Package className="w-3 h-3 mr-1" /> Goods Received
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => openSendOutside(j)} data-testid={`sc-send-btn-${j.code}`} disabled={busy || remaining <= 0}>
                    <Send className="w-3 h-3 mr-1" /> Send Outside
                  </Button>
                )
              )}
              {/* Assembly-only: Issue Materials from SFG BOM */}
              {j.current_stage === "assembly" && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => openIssueMaterials(j)} data-testid={`issue-materials-btn-${j.code}`} disabled={busy}>
                  <Package className="w-3 h-3 mr-1" /> Issue Materials (BOM)
                </Button>
              )}
              {pendingReceive ? (
                <Button size="sm" className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700" onClick={() => receiveJob(j)} data-testid={`receive-btn-${j.code}`} disabled={busy}>
                  <Package className="w-3 h-3 mr-1" /> Receive
                </Button>
              ) : remaining <= 0 && hasInflight ? (
                <Link to="/quality-inspections">
                  <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600" data-testid={`awaiting-qc-link-${j.code}`}>
                    <Clock className="w-3 h-3 mr-1" /> Awaiting QC → Inspect
                  </Button>
                </Link>
              ) : (
                <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => openSendForQc(j)} data-testid={`issue-btn-${j.code}`} disabled={busy || remaining <= 0}>
                  <ShieldCheck className="w-3 h-3 mr-1" /> Issue
                </Button>
              )}
              {stage.key === "final_inspection" && (
                <Link
                  to={`/quality/incoming-inspection?${new URLSearchParams({
                    stage: "final_inspection", job_id: j.id, job_code: j.code,
                    part_number: item.code || "", part_name: item.name || "",
                  }).toString()}`}
                  data-testid={`fi-detailed-inspection-btn-${j.code}`}
                >
                  <Button size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50">
                    <Ruler className="w-3 h-3 mr-1" /> Detailed Inspection
                  </Button>
                </Link>
              )}
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setActive(j); setHistoryOpen(true); }} data-testid={`history-btn-${j.code}`}>
                <History className="w-3.5 h-3.5 text-slate-500" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => cancelJob(j)} data-testid={`cancel-btn-${j.code}`}>
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </Button>
            </>
          )}
          {isTerminalFinished && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setActive(j); setHistoryOpen(true); }} data-testid={`history-btn-${j.code}`}>
              <History className="w-3 h-3 mr-1" /> View History
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Single-stage list-view row renderer — same underlying job data and
  // handlers as renderJobCard above, laid out as table columns instead
  // (Job ID / Name / Batch No / Total / Moved / Remaining / Status /
  // Issue / Actions) per stage, for every stage. Also used for the
  // "Sent Outside" section (a job with an open Job Work DC at this stage),
  // where it shows the DC chip and swaps Issue for Goods Received.
  const renderJobRow = (j, stage) => {
    const item = itemMap[j.current_item_id] || itemMap[j.fg_item_id] || itemMap[j.raw_item_id] || {};
    const isTerminalFinished = stage.key === "finished";
    const isActive = j.status === "active";
    const ind = qcIndicator(j);
    const IndIcon = ind.icon;
    const total = Number(j.stage_input_qty ?? j.current_qty ?? 0);
    const inflight = inflightByJob[j.id] || 0;
    const remaining = Number(j.current_qty ?? 0);
    const moved = Math.max(0, total - remaining - inflight);
    const hasInflight = inflight > 0;
    const pendingReceive = !!j.parent_job_id && !j.received_at && j.status === "active" && stage.key !== "rework" && stage.key !== "finished";
    const showActions = !isTerminalFinished && isActive;
    const scDc = SUBCONTRACT_STAGE_KEYS.includes(stage.key) ? activeScDcByJobStage[`${j.id}|${stage.key}`] : null;

    return (
      <tr key={j.id} className={`border-t ${pendingReceive ? "bg-cyan-50/50 border-cyan-100" : hasInflight ? "bg-amber-50/50 border-amber-100" : "border-slate-100 hover:bg-slate-50/60"}`} data-testid={`job-row-${j.code}`}>
        <td className="py-2.5 px-3 align-top">
          <div className="mono text-xs text-slate-500 whitespace-nowrap">
            {j.code}
            {j.parent_job_id && <span className="ml-1 text-purple-600" title="Child batch">↳</span>}
          </div>
        </td>
        <td className="py-2.5 px-3 align-top">
          <div className="text-sm font-medium text-slate-800">{item.name || "—"}</div>
          {j.current_item_id && itemMap[j.current_item_id] && j.current_item_id !== j.raw_item_id && (
            <div className="text-[10px] text-blue-700 mono">{itemMap[j.current_item_id].code}</div>
          )}
        </td>
        <td className="py-2.5 px-3 align-top text-xs text-slate-600 mono whitespace-nowrap">
          <Hash className="inline w-3 h-3 mr-0.5 text-slate-400" />{j.batch_no || "—"}
        </td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs text-slate-700" data-testid={`qty-total-${j.code}`}>{total}</td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs text-emerald-700" data-testid={`qty-moved-${j.code}`}>{moved}</td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs font-semibold text-blue-800 whitespace-nowrap" data-testid={`qty-remaining-${j.code}`}>
          {remaining} <span className="text-slate-400 font-normal">{item.uom || ""}</span>
        </td>
        <td className="py-2.5 px-3 align-top">
          <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold border whitespace-nowrap ${ind.tone}`} data-testid={`qc-indicator-${j.code}`}>
            <IndIcon className="w-3 h-3" /> {ind.label}
          </div>
          {hasInflight && (
            <button type="button" className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap flex items-center gap-0.5 hover:underline" onClick={() => openPendingList(j)} data-testid={`inflight-chip-${j.code}`} title="Edit pending batch(es)">
              Awaiting QC: {inflight} {item.uom || ""} <Pencil className="w-2.5 h-2.5" />
            </button>
          )}
          {pendingReceive && <div className="text-[10px] text-cyan-700 mt-0.5 whitespace-nowrap" data-testid={`pending-receive-chip-${j.code}`}>Pending Receive</div>}
          {j.current_stage === "assembly" && j.materials_issued && <div className="text-[10px] text-indigo-700 mt-0.5 whitespace-nowrap" data-testid={`materials-issued-chip-${j.code}`}>Materials Issued</div>}
          {scDc && (
            <div className="text-[10px] text-orange-700 mt-0.5 whitespace-nowrap flex items-center gap-0.5" data-testid={`sc-sent-chip-${j.code}`}>
              <Send className="w-2.5 h-2.5" /> Sent Outside · {scDc.code}
            </div>
          )}
        </td>
        <td className="py-2.5 px-3 align-top">
          {!showActions ? (
            <span className="text-slate-300 text-xs">—</span>
          ) : (
            <div className="flex flex-col items-start gap-1">
              {j.current_stage === "assembly" && (
                <Button size="sm" variant="outline" className="h-6 text-[11px] whitespace-nowrap border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => openIssueMaterials(j)} data-testid={`issue-materials-btn-${j.code}`} disabled={busy}>
                  <Package className="w-3 h-3 mr-1" /> Materials (BOM)
                </Button>
              )}
              {scDc ? (
                <Button size="sm" variant="outline" className="h-7 text-xs whitespace-nowrap border-orange-400 text-orange-700 hover:bg-orange-50" onClick={() => openReceiveGoods(scDc)} data-testid={`sc-receive-btn-${j.code}`} disabled={busy}>
                  <Package className="w-3 h-3 mr-1" /> Goods Received
                </Button>
              ) : pendingReceive ? (
                <Button size="sm" className="h-7 text-xs whitespace-nowrap bg-cyan-600 hover:bg-cyan-700" onClick={() => receiveJob(j)} data-testid={`receive-btn-${j.code}`} disabled={busy}>
                  <Package className="w-3 h-3 mr-1" /> Receive
                </Button>
              ) : remaining <= 0 && hasInflight ? (
                <Link to="/quality-inspections">
                  <Button size="sm" className="h-7 text-xs whitespace-nowrap bg-amber-500 hover:bg-amber-600" data-testid={`awaiting-qc-link-${j.code}`}>
                    <Clock className="w-3 h-3 mr-1" /> Awaiting QC
                  </Button>
                </Link>
              ) : (
                <Button size="sm" className="h-7 text-xs whitespace-nowrap bg-blue-600 hover:bg-blue-700" onClick={() => openSendForQc(j)} data-testid={`issue-btn-${j.code}`} disabled={busy || remaining <= 0}>
                  <ShieldCheck className="w-3 h-3 mr-1" /> Issue
                </Button>
              )}
              {stage.key === "final_inspection" && (
                <Link
                  to={`/quality/incoming-inspection?${new URLSearchParams({
                    stage: "final_inspection", job_id: j.id, job_code: j.code,
                    part_number: item.code || "", part_name: item.name || "",
                  }).toString()}`}
                  data-testid={`fi-detailed-inspection-btn-${j.code}`}
                >
                  <Button size="sm" variant="outline" className="h-7 text-[11px] whitespace-nowrap border-orange-300 text-orange-700 hover:bg-orange-50">
                    <Ruler className="w-3 h-3 mr-1" /> Detailed Inspection
                  </Button>
                </Link>
              )}
            </div>
          )}
        </td>
        <td className="py-2.5 px-3 align-top">
          <div className="flex items-center gap-1">
            {isActive && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openAdjustQty(j)} data-testid={`edit-qty-btn-${j.code}`} title="Edit remaining quantity">
                <Pencil className="w-3.5 h-3.5 text-slate-500" />
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setActive(j); setHistoryOpen(true); }} data-testid={`history-btn-${j.code}`} title="History">
              <History className="w-3.5 h-3.5 text-slate-500" />
            </Button>
            {isActive && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => cancelJob(j)} data-testid={`cancel-btn-${j.code}`} title="Cancel job">
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Raw Material page row renderer — one row per RM Store item. No job
  // exists yet, so Batch No/Total/Moved/In Process are all degenerate
  // (Total==Remaining, Moved is always 0), and Actions only has Edit —
  // there's no job to view history of or cancel.
  const renderRawMaterialRow = (r) => {
    const activeDc = activeManualScDcByItem[r.item_id];
    return (
      <tr key={r.item_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`rm-row-${r.item.code}`}>
        <td className="py-2.5 px-3 align-top mono text-xs text-slate-500 whitespace-nowrap">{r.item.code}</td>
        <td className="py-2.5 px-3 align-top text-sm font-medium text-slate-800">{r.item.name || "—"}</td>
        <td className="py-2.5 px-3 align-top text-xs text-slate-400">—</td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs text-slate-700">{r.qty}</td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs text-emerald-700">0</td>
        <td className="py-2.5 px-3 align-top text-right mono text-xs font-semibold text-blue-800 whitespace-nowrap">
          {r.qty} <span className="text-slate-400 font-normal">{r.item.uom || ""}</span>
        </td>
        <td className="py-2.5 px-3 align-top">
          <div className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded uppercase tracking-wide font-semibold border bg-slate-100 text-slate-700 border-slate-300 whitespace-nowrap">
            <Package className="w-3 h-3" /> In Stock
          </div>
          {activeDc && (
            <div className="text-[10px] text-orange-700 mt-0.5 whitespace-nowrap">Sent Outside · {activeDc.code}</div>
          )}
        </td>
        <td className="py-2.5 px-3 align-top">
          <Button size="sm" className="h-7 text-xs whitespace-nowrap bg-blue-600 hover:bg-blue-700" onClick={() => startIssue(r.item_id)} data-testid={`rm-issue-btn-${r.item.code}`} disabled={busy}>
            <ShieldCheck className="w-3 h-3 mr-1" /> Issue
          </Button>
        </td>
        <td className="py-2.5 px-3 align-top">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openRawEdit(r)} data-testid={`rm-edit-btn-${r.item.code}`} title="Edit quantity">
            <Pencil className="w-3.5 h-3.5 text-slate-500" />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="production-flow-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          {isSingleStage && (
            <button
              type="button"
              onClick={() => navigate("/production-flow")}
              className="text-xs text-blue-600 hover:underline mb-1 inline-flex items-center gap-1"
              data-testid="back-to-all-stages"
            >
              ← All Stages
            </button>
          )}
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {isSingleStage ? "Production Flow" : "Operations"}
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }} data-testid="production-flow-heading">
            {isSingleStage ? currentStage.label : "Production Flow"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isRawMaterial
              ? "Raw material available for issue to Machine Shop, or send outside for job work before it enters the flow."
              : isSingleStage
              ? (currentStage.key === "finished"
                  ? "Finished batches that have passed Final Inspection."
                  : <>Issue material in, receive arrivals from the previous stage, or send material outside for job work. Complete inspections in <Link to="/quality-inspections" className="text-blue-600 underline">Quality Inspections</Link>.</>)
              : <>Every stage transition requires a Quality Inspection. Click <strong>Send for QC</strong> to enqueue a job; complete the inspection in the <Link to="/quality-inspections" className="text-blue-600 underline">Quality Inspections</Link> module.</>}
          </p>
        </div>
        {isRawMaterial ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-blue-300 text-blue-800 hover:bg-blue-50" onClick={openRawMaterialDirectIssue} data-testid="stage-issue-btn">
              <Send className="w-4 h-4 mr-1.5" /> Issue
            </Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => openRawMaterialDc()} data-testid="stage-create-dc-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Job Work DC
            </Button>
          </div>
        ) : isSingleStage ? (
          currentStage.key !== "finished" && (
            <div className="flex items-center gap-2">
              <Button variant="outline" className="border-blue-300 text-blue-800 hover:bg-blue-50" onClick={() => openStageIssue(currentStage.key)} data-testid="stage-issue-btn">
                <Send className="w-4 h-4 mr-1.5" /> Issue
              </Button>
              <Button variant="outline" className="border-cyan-300 text-cyan-800 hover:bg-cyan-50" onClick={() => openReceiveForStage(currentStage.key)} data-testid="stage-receive-btn">
                <Package className="w-4 h-4 mr-1.5" /> Receive
                {pendingReceiveCountByStage[currentStage.key] > 0 && (
                  <span className="ml-1.5 text-[10px] font-bold bg-cyan-600 text-white rounded-full px-1.5 py-0.5" data-testid="stage-receive-count">
                    {pendingReceiveCountByStage[currentStage.key]}
                  </span>
                )}
              </Button>
              <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => openCreateJobWorkDc(currentStage.key)} data-testid="stage-create-dc-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Create Job Work DC
              </Button>
            </div>
          )
        ) : (
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-cyan-300 text-cyan-800 hover:bg-cyan-50" onClick={() => openDirectIssue(null)} data-testid="direct-issue-btn">
            <Send className="w-4 h-4 mr-1.5" /> Issue to Stage
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => startIssue()} data-testid="issue-material-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Issue Material to Machine Shop
          </Button>
        </div>
        )}
      </div>

      {/* Issue Raw Material → Machine Shop — not wrapped in a SheetTrigger
          since it's opened from several places (the overview header, the
          Machine Shop stage page, and each Raw Material row's Issue button
          via startIssue()); state (issueOpen) alone controls it. */}
      <Sheet open={issueOpen} onOpenChange={setIssueOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader><SheetTitle>Issue Raw Material → Machine Shop</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4">
            <div>
              <Label>Raw Material *</Label>
              <Select value={issueForm.raw_item_id} onValueChange={(v) => setIssueForm({ ...issueForm, raw_item_id: v })}>
                <SelectTrigger data-testid="issue-raw-select"><SelectValue placeholder="Select raw material" /></SelectTrigger>
                <SelectContent>
                  {rawItems.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.code} — {it.name} ({rmStock(it.id)} {it.uom} in RM)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Finished Product (optional)</Label>
              <Select
                value={issueForm.fg_item_id || ""}
                onValueChange={(v) => {
                  // Routing (Routing editor) says which RAW item and Machine
                  // Shop part number this FG uses — auto-fill both from it
                  // so the operator doesn't have to look the codes up (e.g.
                  // PP242500 → raw PP242500RM, Machine Shop PP242500MC).
                  const routing = routingByFg[v];
                  setIssueForm((f) => ({
                    ...f,
                    fg_item_id: v,
                    raw_item_id: routing?.stage_outputs?.raw_material || f.raw_item_id,
                    output_item_id: routing?.stage_outputs?.machine_shop || f.output_item_id,
                  }));
                }}
              >
                <SelectTrigger data-testid="issue-fg-select"><SelectValue placeholder="Link to a finished product" /></SelectTrigger>
                <SelectContent>
                  {fgItems.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Usually not needed — MRP Run finds the right Finished Good automatically from the issued part's "Linked Finished Good" (set once in Items & BOM). Picking one here also auto-fills the Raw Material and Issue-As-Part # below from its Routing, if one is set up.</p>
            </div>
            <div>
              <Label>Issue As Part # (optional) <span className="text-[10px] font-normal text-slate-500">— leave blank to keep the raw material's own code</span></Label>
              <Select value={issueForm.output_item_id || ""} onValueChange={(v) => setIssueForm({ ...issueForm, output_item_id: v })}>
                <SelectTrigger data-testid="issue-output-item-select"><SelectValue placeholder="Same as raw material" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {items.filter((i) => i.type === "MS").map((it) => (
                    <SelectItem key={it.id} value={it.id} data-testid={`issue-output-opt-${it.code}`}>{it.code} — {it.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Converts the part number right at issue — e.g. a raw bar issued and immediately recorded as the machined part it becomes.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Qty Issued *</Label><Input type="number" value={issueForm.raw_qty} onChange={(e) => setIssueForm({ ...issueForm, raw_qty: parseFloat(e.target.value || 0) })} data-testid="issue-qty-input" /></div>
              <div><Label>Batch No</Label><Input value={issueForm.batch_no} onChange={(e) => setIssueForm({ ...issueForm, batch_no: e.target.value })} placeholder="Auto if blank" /></div>
              <div><Label>Operator</Label><Input value={issueForm.operator} onChange={(e) => setIssueForm({ ...issueForm, operator: e.target.value })} /></div>
              <div><Label>Machine</Label><Input value={issueForm.machine} onChange={(e) => setIssueForm({ ...issueForm, machine: e.target.value })} placeholder="LATHE-01" /></div>
              <div className="col-span-2"><Label>Expected Completion</Label><Input type="date" value={issueForm.expected_completion} onChange={(e) => setIssueForm({ ...issueForm, expected_completion: e.target.value })} /></div>
            </div>
            {issueForm.raw_item_id && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
                RM Stock available: <span className="mono font-semibold text-slate-700">{rmStock(issueForm.raw_item_id)} {itemMap[issueForm.raw_item_id]?.uom}</span>
              </div>
            )}
          </div>
          <SheetFooter className="mt-6">
            <Button onClick={submitIssue} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-issue-btn">Issue & Create Job</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isSingleStage ? (
        /* Single-stage page — full-width table. Same columns for every
           stage, including Raw Material (which lists RM Store items
           instead of jobs, since no job exists yet at that point). */
        <div className="space-y-4" data-testid={`stage-page-${currentStage.key}`}>
          <Input
            value={stageSearch[currentStage.key] || ""}
            onChange={(e) => setStageQuery(currentStage.key, e.target.value)}
            placeholder="Search part / batch…"
            className="max-w-sm"
            data-testid={`stage-search-${currentStage.key}`}
          />
          {/* Work stages open on "Active only"; Status and started-on date
              narrow further, and "Load All" shows every job ever parked at
              this stage (active + closed / cancelled). */}
          {!isRawMaterial && (
            <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`stage-filter-bar-${currentStage.key}`}>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Status</Label>
                <Select
                  value={getStageFilter(currentStage.key).status}
                  onValueChange={(v) => setStageFilter(currentStage.key, { status: v })}
                >
                  <SelectTrigger className="h-8 w-36" data-testid={`stage-filter-status-${currentStage.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All records</SelectItem>
                    <SelectItem value="active">Active only</SelectItem>
                    <SelectItem value="closed">Closed / Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Started From</Label>
                <Input
                  type="date"
                  className="h-8 w-36"
                  value={getStageFilter(currentStage.key).from}
                  onChange={(e) => setStageFilter(currentStage.key, { from: e.target.value })}
                  data-testid={`stage-filter-from-${currentStage.key}`}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Started To</Label>
                <Input
                  type="date"
                  className="h-8 w-36"
                  value={getStageFilter(currentStage.key).to}
                  onChange={(e) => setStageFilter(currentStage.key, { to: e.target.value })}
                  data-testid={`stage-filter-to-${currentStage.key}`}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => clearStageFilter(currentStage.key)}
                disabled={!isStageFilterActive(currentStage.key)}
                data-testid={`stage-filter-load-all-${currentStage.key}`}
              >
                Load All
              </Button>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`stage-table-${currentStage.key}`}>
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50">
                    <th className="py-3 px-3 font-semibold">{isRawMaterial ? "Item Code" : "Job ID"}</th>
                    <th className="py-3 px-3 font-semibold">Name</th>
                    <th className="py-3 px-3 font-semibold">Batch No</th>
                    <th className="py-3 px-3 font-semibold text-right">Total</th>
                    <th className="py-3 px-3 font-semibold text-right">Moved</th>
                    <th className="py-3 px-3 font-semibold text-right">Remaining</th>
                    <th className="py-3 px-3 font-semibold">In Process</th>
                    <th className="py-3 px-3 font-semibold">Issue</th>
                    <th className="py-3 px-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isRawMaterial ? (
                    rmRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-sm text-slate-500 italic">
                          {stageSearch.raw_material ? "No items match this search" : "No raw material in stock"}
                        </td>
                      </tr>
                    ) : rmRows.map((r) => renderRawMaterialRow(r))
                  ) : (() => {
                    const stageJobs = stageJobsForView(currentStage.key).filter(
                      (j) => stageQueryMatches(j, currentStage.key) && stageFilterMatches(j, currentStage.key)
                    );
                    if (stageJobs.length === 0) {
                      return (
                        <tr>
                          <td colSpan={9} className="py-12 text-center text-sm text-slate-500 italic">
                            {stageSearch[currentStage.key] || isStageFilterActive(currentStage.key)
                              ? "No jobs match this search/filter"
                              : currentStage.key === "finished" ? "No finished batches yet" : "No jobs have been at this stage"}
                          </td>
                        </tr>
                      );
                    }
                    return stageJobs.map((j) => renderJobRow(j, currentStage));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sent Outside — jobs with an open Job Work DC at this stage.
              Kept out of the store table above (and its count) entirely;
              Goods Received here closes the DC, raises the GIN, and moves
              the job back into the main store list. */}
          {!isRawMaterial && SUBCONTRACT_STAGE_KEYS.includes(currentStage.key) && (() => {
            const sentOutsideJobs = sentOutsideCols[currentStage.key].filter((j) => stageQueryMatches(j, currentStage.key));
            if (sentOutsideJobs.length === 0) return null;
            return (
              <div className="bg-orange-50/40 border border-orange-200 rounded-lg overflow-hidden" data-testid={`sent-outside-section-${currentStage.key}`}>
                <div className="px-3 py-2 text-xs uppercase tracking-[0.14em] text-orange-800 font-semibold flex items-center gap-1.5 border-b border-orange-200">
                  <Send className="w-3.5 h-3.5" /> Sent Outside — Awaiting GIN
                  <span className="text-[10px] mono px-2 py-0.5 rounded-full bg-white/70" data-testid={`sent-outside-count-${currentStage.key}`}>{sentOutsideJobs.length}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`sent-outside-table-${currentStage.key}`}>
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-orange-50">
                        <th className="py-3 px-3 font-semibold">Job ID</th>
                        <th className="py-3 px-3 font-semibold">Name</th>
                        <th className="py-3 px-3 font-semibold">Batch No</th>
                        <th className="py-3 px-3 font-semibold text-right">Total</th>
                        <th className="py-3 px-3 font-semibold text-right">Moved</th>
                        <th className="py-3 px-3 font-semibold text-right">Remaining</th>
                        <th className="py-3 px-3 font-semibold">In Process</th>
                        <th className="py-3 px-3 font-semibold">Issue</th>
                        <th className="py-3 px-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>{sentOutsideJobs.map((j) => renderJobRow(j, currentStage))}</tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <>
          {/* Stage Kanban */}
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 pb-2">
            <div className="flex gap-3 min-w-max" data-testid="production-kanban">
              {STAGES.map((stage) => {
                const stageJobs = cols[stage.key].filter((j) => stageQueryMatches(j, stage.key));
                const sentOutsideJobs = SUBCONTRACT_STAGE_KEYS.includes(stage.key)
                  ? sentOutsideCols[stage.key].filter((j) => stageQueryMatches(j, stage.key))
                  : [];
                return (
                <div key={stage.key} className={`w-64 shrink-0 rounded-lg border ${stage.tone} p-3`} data-testid={`stage-col-${stage.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wide font-bold">{stage.label}</div>
                    <span className="text-[10px] mono px-2 py-0.5 rounded-full bg-white/60" data-testid={`stage-count-${stage.key}`}>{stageJobs.length}{stageSearch[stage.key] ? `/${cols[stage.key].length}` : ""}</span>
                  </div>
                  {/* Phase 33 — per-stage real-time search */}
                  <Input
                    value={stageSearch[stage.key] || ""}
                    onChange={(e) => setStageQuery(stage.key, e.target.value)}
                    placeholder="Search part / batch…"
                    className="h-7 mb-2 text-[11px] bg-white/70"
                    data-testid={`stage-search-${stage.key}`}
                  />
                  <div className="space-y-2 min-h-[140px]">
                    {stageJobs.length === 0 && (
                      <div className="text-xs text-slate-500 italic text-center py-6">
                        {stageSearch[stage.key]
                          ? "No jobs match this search"
                          : stage.key === "rework" ? "No rework items" : stage.key === "finished" ? "No finished batches" : "No active jobs"}
                      </div>
                    )}
                    {stageJobs.map((j) => renderJobCard(j, stage))}
                  </div>
                  {/* Sent Outside — an open Job Work DC exists at this stage;
                      kept out of the store list/count above until Goods
                      Received (GIN) moves the job back in. */}
                  {sentOutsideJobs.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-orange-200/70" data-testid={`sent-outside-col-${stage.key}`}>
                      <div className="text-[10px] uppercase tracking-wide font-bold text-orange-800 flex items-center gap-1 mb-2">
                        <Send className="w-3 h-3" /> Sent Outside
                        <span className="text-[10px] mono px-1.5 py-0.5 rounded-full bg-white/70" data-testid={`sent-outside-count-${stage.key}`}>{sentOutsideJobs.length}</span>
                      </div>
                      <div className="space-y-2">{sentOutsideJobs.map((j) => renderJobCard(j, stage))}</div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Closed jobs strip */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Recently Closed Jobs</div>
            {jobs.filter((j) => j.status !== "active").length === 0 ? (
              <div className="text-xs text-slate-400 italic">None yet — completed jobs land here.</div>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {jobs.filter((j) => j.status !== "active").slice(0, 15).map((j) => {
                  const item = itemMap[j.fg_item_id] || itemMap[j.raw_item_id] || {};
                  return (
                    <div key={j.id} className="shrink-0 w-56 border border-slate-200 rounded-md p-2 text-xs" data-testid={`closed-job-${j.code}`}>
                      <div className="flex justify-between items-center">
                        <span className="mono text-[10px] text-slate-500">{j.code}</span>
                        <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${j.status === "closed" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{j.status}</span>
                      </div>
                      <div className="text-slate-800 truncate font-medium mt-0.5">{item.name || "—"}</div>
                      <div className="text-slate-500 mono mt-0.5">{j.current_qty} {item.uom || ""}</div>
                      <button onClick={() => { setActive(j); setHistoryOpen(true); }} className="text-blue-600 text-[11px] hover:underline mt-1">View history →</button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      {/* Send-for-QC dialog: operator declares output part number + destination store (Phase 28) */}
      {/* Phase 33.1 — Direct Issue to Stage */}
      <Dialog open={directOpen} onOpenChange={setDirectOpen}>
        <DialogContent className="max-w-md" data-testid="direct-issue-dialog">
          <DialogHeader>
            <DialogTitle>
              Issue to Stage
              <div className="text-xs font-normal text-slate-500 mt-1">
                Ship an item straight to any stage — independent of the default flow. It lands at the destination directly, without QC.
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Part Number / Name *</Label>
              <Input
                placeholder="Search by code or name…"
                value={directItemQuery}
                onChange={(e) => setDirectItemQuery(e.target.value)}
                className="mb-1"
                data-testid="direct-item-search"
              />
              <Select value={directForm.item_id} onValueChange={(v) => setDirectForm({ ...directForm, item_id: v })}>
                <SelectTrigger data-testid="direct-item-select"><SelectValue placeholder="Select an item" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {directItemMatches.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-500">No items match.</div>
                  ) : directItemMatches.map((it) => (
                    <SelectItem key={it.id} value={it.id} data-testid={`direct-item-opt-${it.code}`}>
                      <span className="mono text-[10px] text-slate-500">{it.code}</span> · {it.name} <span className="text-[10px] text-slate-400">({it.type})</span>
                      {directFromRm && <span className="text-[10px] text-emerald-700 ml-1">· {directStockQty[it.id] || 0} in stock</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quantity *</Label>
                <Input type="number" step="0.01" min="0" value={directForm.qty} onChange={(e) => setDirectForm({ ...directForm, qty: e.target.value })} data-testid="direct-qty-input" />
              </div>
              <div>
                <Label>Source Store *</Label>
                <Select value={directForm.source_store} onValueChange={(v) => setDirectForm({ ...directForm, source_store: v, dest_stage: issueDestFor(v, directLockedStage), ...(directFromRm ? { item_id: "" } : {}) })}>
                  <SelectTrigger data-testid="direct-source-store-select"><SelectValue placeholder="Where from?" /></SelectTrigger>
                  <SelectContent>
                    {ALL_STORES.map((s) => (
                      <SelectItem key={s.key} value={s.key} data-testid={`direct-source-opt-${s.key}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase 35.5 — live availability at the chosen source store */}
            {directForm.item_id && directForm.source_store && (
              <div className={`text-xs rounded-md border px-3 py-2 flex items-center justify-between ${
                directAvailable === null ? "border-slate-200 bg-slate-50 text-slate-500"
                  : directAvailable <= 0 ? "border-red-200 bg-red-50 text-red-700"
                  : Number(directForm.qty) > directAvailable ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`} data-testid="direct-availability">
                <span>
                  Available at source: <strong className="mono">{directAvailable === null ? "—" : directAvailable}</strong>
                </span>
                {directForm.qty && directAvailable !== null && Number(directForm.qty) > directAvailable && (
                  <span className="font-semibold">Short by {(Number(directForm.qty) - directAvailable).toFixed(2)}</span>
                )}
              </div>
            )}

            <div>
              <Label>Destination Stage *</Label>
              <Select value={directForm.dest_stage} disabled={!directFromRm} onValueChange={(v) => setDirectForm({ ...directForm, dest_stage: v })}>
                <SelectTrigger data-testid="direct-dest-stage-select"><SelectValue placeholder={directForm.source_store ? "No next stage (Finished Goods is final)" : "Send it to…"} /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`direct-dest-opt-${s.key}`}>
                      {s.label}{directLocked && s.key === directLockedStage ? " (current stage)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                {directFromRm
                  ? "Set automatically to the next stage in the Production Flow — change it to send to any stage. "
                  : "Set automatically to the next stage in the Production Flow. "}
                A new job card will be created at this stage directly — no QC inspection is raised.
              </p>
            </div>

            <div>
              <Label>Work Order (optional)</Label>
              <Select value={directForm.work_order_id || "__none__"} onValueChange={(v) => setDirectForm({ ...directForm, work_order_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="direct-wo-select"><SelectValue placeholder="Link to WO (optional)" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__">— None —</SelectItem>
                  {directWorkOrders.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`direct-wo-opt-${w.code}`}>
                      {w.code} · {itemMap[w.fg_item_id]?.name || w.fg_item_code || "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason / Notes (optional)</Label>
              {directFromRm ? (
                <Textarea
                  rows={3}
                  value={directForm.notes}
                  onChange={(e) => setDirectForm({ ...directForm, notes: e.target.value })}
                  placeholder="Why is this being sent directly? Any batch reference…"
                  data-testid="direct-notes-input"
                />
              ) : (
                <Input
                  value={directForm.notes}
                  onChange={(e) => setDirectForm({ ...directForm, notes: e.target.value })}
                  placeholder="Why is this being sent directly? Any batch reference…"
                  data-testid="direct-notes-input"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDirectOpen(false)}>Cancel</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={submitDirectIssue} disabled={busy} data-testid="direct-issue-submit">
              <Send className="w-4 h-4 mr-1.5" /> Issue to {STAGE_LABEL[directForm.dest_stage] || "Stage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={qcDialogOpen} onOpenChange={setQcDialogOpen}>
        <DialogContent className="max-w-md" data-testid="send-qc-dialog">
          <DialogHeader>
            <DialogTitle data-testid="send-qc-title">
              Issue {qcTarget?.code}
              <div className="text-xs font-normal text-slate-500 mt-1" data-testid="send-qc-subtitle">
                {qcTarget && <>From: <strong>{STAGE_LABEL[qcTarget.current_stage]}</strong> · {qcTarget.current_qty} units → To: <strong>{STAGE_LABEL[qcForm.dest_stage] || "—"}</strong></>}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(qcTarget?._pick_jobs || []).length > 1 && (
              <div>
                <Label>Job *</Label>
                <Select value={qcTarget.id} onValueChange={(id) => { const j = qcTarget._pick_jobs.find((x) => x.id === id); if (j) openSendForQc(j, qcTarget._pick_jobs); }}>
                  <SelectTrigger data-testid="qc-job-select"><SelectValue placeholder="Select job" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {qcTarget._pick_jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id} data-testid={`qc-job-opt-${j.code}`}>
                        {j.code} · {(itemMap[j.current_item_id] || itemMap[j.fg_item_id] || {}).name || "—"} · {j.current_qty} left
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2" data-testid="send-qc-info-tiles">
              <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5">
                <div className="uppercase text-[9px] tracking-wide text-blue-700">Remaining at this stage</div>
                <div className="mono text-sm font-semibold text-blue-900" data-testid="qc-tile-remaining">{qcTarget?.current_qty || 0}</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                <div className="uppercase text-[9px] tracking-wide text-slate-500">Default Next stage</div>
                <div className="text-sm font-semibold text-slate-800" data-testid="qc-tile-next-stage">{STAGE_LABEL[qcTarget?._next_stage] || "—"}</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                <div className="uppercase text-[9px] tracking-wide text-slate-500">Default Store</div>
                <div className="mono text-sm font-semibold text-slate-800 break-all" data-testid="qc-tile-default-store">{qcTarget?._default_store || "—"}</div>
              </div>
            </div>

            <div>
              <Label>Current Part Number</Label>
              <Select value={qcForm.source_pick} onValueChange={(v) => setQcForm({ ...qcForm, source_pick: v, source_item_id: v.split("|")[1] })}>
                <SelectTrigger className="mono" data-testid="qc-current-part-select">
                  <SelectValue placeholder="Select current part">
                    {itemMap[qcForm.source_item_id] ? `${itemMap[qcForm.source_item_id].code} — ${itemMap[qcForm.source_item_id].name}` : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-500">From Stage · {STAGE_LABEL[qcTarget?.current_stage]}</SelectLabel>
                    {qcCurrentPartOptions.stage.map((it) => (
                      <SelectItem key={`stage-${it.id}`} value={`stage|${it.id}`} data-testid={`qc-current-part-stage-opt-${it.code}`}>
                        {it.code} — {it.name}{it.id === (qcTarget?.current_item_id || qcTarget?.raw_item_id) ? " (this job)" : ""}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-500">From Stock · {qcCurrentPartOptions.store || "—"}</SelectLabel>
                    {qcCurrentPartOptions.stock.length === 0 ? (
                      <div className="px-2 py-2 text-xs text-slate-500">No stock in {qcCurrentPartOptions.store || "this store"}.</div>
                    ) : qcCurrentPartOptions.stock.map((it) => (
                      <SelectItem key={`stock-${it.id}`} value={`stock|${it.id}`} data-testid={`qc-current-part-stock-opt-${it.code}`}>
                        {it.code} — {it.name} <span className="text-[10px] text-slate-400">({it._qty} in stock)</span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Destination Stage * <span className="text-[10px] font-normal text-slate-500">(select any stage ahead — you can skip)</span></Label>
              <Select value={qcForm.dest_stage} onValueChange={onChangeDestStage}>
                <SelectTrigger data-testid="qc-dest-stage-select"><SelectValue placeholder="Select destination stage" /></SelectTrigger>
                <SelectContent>
                  {(qcTarget?._forward_stages || []).map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`qc-dest-stage-opt-${s.key}`}>
                      {s.label}{s.key === qcTarget?._next_stage && s.key !== "finished" ? " (Next)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {qcTarget && qcForm.dest_stage && (() => {
                const curIdx = STAGES.findIndex((s) => s.key === qcTarget.current_stage);
                const destIdx = STAGES.findIndex((s) => s.key === qcForm.dest_stage);
                const skipped = destIdx > curIdx + 1 && !isDefaultNext(qcTarget.current_stage, qcForm.dest_stage) ? STAGES.slice(curIdx + 1, destIdx).map((s) => s.label) : [];
                return skipped.length > 0 ? (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="skip-warning">
                    ⚠ Skipping {skipped.length} stage{skipped.length === 1 ? "" : "s"}: <strong>{skipped.join(" → ")}</strong>
                  </p>
                ) : null;
              })()}
            </div>

            <div>
              <Label>Quantity to Move *</Label>
              <Input
                type="number"
                min="0"
                max={qcTarget?.current_qty || 0}
                step="0.01"
                required
                value={qcForm.moved_qty}
                onChange={(e) => setQcForm({ ...qcForm, moved_qty: e.target.value })}
                placeholder={`Enter qty (up to ${qcTarget?.current_qty || 0})`}
                data-testid="qc-moved-qty-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Only this quantity is issued. The remainder stays at <strong>{STAGE_LABEL[qcTarget?.current_stage]}</strong> for further work.
              </p>
            </div>

            <div>
              <Label>Output Part Number (produced at this stage) *</Label>
              <Select value={qcForm.output_item_id} onValueChange={(v) => setQcForm({ ...qcForm, output_item_id: v })}>
                <SelectTrigger data-testid="qc-output-item-select"><SelectValue placeholder={`Select ${STAGE_ITEM_TYPE[qcForm.dest_stage] || ""} part`} /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {/* The Routing/BOM part pre-filled for this stage always stays
                      listed, even if its item type differs from the stage's. */}
                  {qcRoutingOutput && qcRoutingOutput.type !== STAGE_ITEM_TYPE[qcForm.dest_stage] && (
                    <SelectItem value={qcRoutingOutput.id} data-testid={`qc-output-opt-${qcRoutingOutput.code}`}>
                      {qcRoutingOutput.code} — {qcRoutingOutput.name} (Routing)
                    </SelectItem>
                  )}
                  {items.filter((i) => i.type === STAGE_ITEM_TYPE[qcForm.dest_stage]).map((it) => (
                    <SelectItem key={it.id} value={it.id} data-testid={`qc-output-opt-${it.code}`}>
                      {it.code} — {it.name}{it.id === qcRoutingOutput?.id ? " (Routing)" : ""}
                    </SelectItem>
                  ))}
                  {!qcRoutingOutput && items.filter((i) => i.type === STAGE_ITEM_TYPE[qcForm.dest_stage]).length === 0 && (
                    <div className="px-2 py-3 text-xs text-slate-500">
                      No items of type <strong>{STAGE_ITEM_TYPE[qcForm.dest_stage]}</strong> configured. Add one in Items master.
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Must be the output type for the destination stage{STAGE_ITEM_TYPE[qcForm.dest_stage] ? <> (<strong>{STAGE_ITEM_TYPE[qcForm.dest_stage]}</strong> for {STAGE_LABEL[qcForm.dest_stage]})</> : null}.
              </p>
            </div>
            <div>
              <Label>Destination Store *</Label>
              <Select value={qcForm.dest_store} onValueChange={(v) => setQcForm({ ...qcForm, dest_store: v })}>
                <SelectTrigger data-testid="qc-dest-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>
                  {ALL_STORES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`qc-dest-opt-${s.key}`}>
                      {s.label}{s.key === STAGE_STORE[qcForm.dest_stage] ? " (Default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Where the passed qty will land after QC. Failed qty always goes to Rework Store.</p>
            </div>
            {qcTarget && qcForm.dest_stage && (() => {
              const curIdx = STAGES.findIndex((s) => s.key === qcTarget.current_stage);
              const destIdx = STAGES.findIndex((s) => s.key === qcForm.dest_stage);
              return destIdx > curIdx + 1 && !isDefaultNext(qcTarget.current_stage, qcForm.dest_stage);
            })() && (
              <div>
                <Label>Skip Reason *</Label>
                <Input
                  value={qcForm.skip_reason}
                  onChange={(e) => setQcForm({ ...qcForm, skip_reason: e.target.value })}
                  placeholder="Why is the batch skipping intermediate stages?"
                  data-testid="qc-skip-reason-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">Logged on the job history and stock-ledger row.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQcDialogOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitSendForQc} disabled={busy} data-testid="send-qc-submit">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Issue → {STAGE_LABEL[qcForm.dest_stage] || "…"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending-batch picker — a job can have more than one partial batch
          awaiting QC at once; pick which one to edit. */}
      <Dialog open={pendingListOpen} onOpenChange={setPendingListOpen}>
        <DialogContent className="max-w-lg" data-testid="pending-list-dialog">
          <DialogHeader>
            <DialogTitle>Pending Batches — {pendingListJob?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="pending-list">
            {pendingInsp.filter((p) => p.job_id === pendingListJob?.id).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50/40 rounded-md px-3 py-2" data-testid={`pending-row-${p.id}`}>
                <div className="min-w-0 text-xs">
                  <div className="font-medium text-slate-800">
                    {STAGE_LABEL[p.stage]} → {STAGE_LABEL[p.next_stage] || "—"}
                  </div>
                  <div className="text-slate-500 mono mt-0.5">
                    {p.pending_qty} · {p.output_item_code || "—"}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="shrink-0" onClick={() => openEditPendingBatch(p, pendingListJob)} data-testid={`pending-edit-btn-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              </div>
            ))}
            {pendingInsp.filter((p) => p.job_id === pendingListJob?.id).length === 0 && (
              <div className="text-xs text-slate-500 italic text-center py-6">Nothing pending — it may have just been inspected.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingListOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit a pending batch — same fields as the Issue dialog, pre-filled
          from the inspection record, so a mistake made at request-qc time
          (wrong qty, wrong output part, wrong destination) can be corrected
          before an inspector completes it. */}
      <Dialog open={editInspOpen} onOpenChange={setEditInspOpen}>
        <DialogContent className="max-w-md" data-testid="edit-pending-dialog">
          <DialogHeader>
            <DialogTitle>
              Edit Pending Batch — {editInspTarget?._job?.code}
              <div className="text-xs font-normal text-slate-500 mt-1">
                {editInspTarget && <>From: <strong>{STAGE_LABEL[editInspTarget.stage]}</strong> → To: <strong>{STAGE_LABEL[editInspForm.dest_stage]}</strong></>}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Destination Stage * <span className="text-[10px] font-normal text-slate-500">(select any stage ahead — you can skip)</span></Label>
              <Select value={editInspForm.dest_stage} onValueChange={onChangeEditDestStage}>
                <SelectTrigger data-testid="edit-insp-dest-stage-select"><SelectValue placeholder="Select destination stage" /></SelectTrigger>
                <SelectContent>
                  {(editInspTarget?._forward_stages || []).map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`edit-insp-dest-stage-opt-${s.key}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editInspTarget && editInspForm.dest_stage && (() => {
                const curIdx = STAGES.findIndex((s) => s.key === editInspTarget.stage);
                const destIdx = STAGES.findIndex((s) => s.key === editInspForm.dest_stage);
                const skipped = destIdx > curIdx + 1 && !isDefaultNext(editInspTarget.stage, editInspForm.dest_stage) ? STAGES.slice(curIdx + 1, destIdx).map((s) => s.label) : [];
                return skipped.length > 0 ? (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="edit-insp-skip-warning">
                    ⚠ Skipping {skipped.length} stage{skipped.length === 1 ? "" : "s"}: <strong>{skipped.join(" → ")}</strong>
                  </p>
                ) : null;
              })()}
            </div>

            <div>
              <Label>Quantity to Move *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editInspForm.moved_qty}
                onChange={(e) => setEditInspForm({ ...editInspForm, moved_qty: e.target.value })}
                data-testid="edit-insp-moved-qty-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Available to reassign: <strong>{Number(editInspTarget?._job?.current_qty || 0) + Number(editInspTarget?.pending_qty || 0)}</strong> (this batch's own qty plus whatever's still at the stage).
              </p>
            </div>

            <div>
              <Label>Output Part Number (produced at this stage) *</Label>
              <Select value={editInspForm.output_item_id} onValueChange={(v) => setEditInspForm({ ...editInspForm, output_item_id: v })}>
                <SelectTrigger data-testid="edit-insp-output-item-select"><SelectValue placeholder={`Select ${STAGE_ITEM_TYPE[editInspForm.dest_stage] || ""} part`} /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {items.filter((i) => i.type === STAGE_ITEM_TYPE[editInspForm.dest_stage]).map((it) => (
                    <SelectItem key={it.id} value={it.id} data-testid={`edit-insp-output-opt-${it.code}`}>
                      {it.code} — {it.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Must be a <strong>{STAGE_ITEM_TYPE[editInspForm.dest_stage]}</strong>-type item (the output type for {STAGE_LABEL[editInspForm.dest_stage]}).
              </p>
            </div>
            <div>
              <Label>Destination Store *</Label>
              <Select value={editInspForm.dest_store} onValueChange={(v) => setEditInspForm({ ...editInspForm, dest_store: v })}>
                <SelectTrigger data-testid="edit-insp-dest-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>
                  {ALL_STORES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`edit-insp-dest-opt-${s.key}`}>
                      {s.label}{s.key === STAGE_STORE[editInspForm.dest_stage] ? " (Default)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editInspTarget && editInspForm.dest_stage && (() => {
              const curIdx = STAGES.findIndex((s) => s.key === editInspTarget.stage);
              const destIdx = STAGES.findIndex((s) => s.key === editInspForm.dest_stage);
              return destIdx > curIdx + 1 && !isDefaultNext(editInspTarget.stage, editInspForm.dest_stage);
            })() && (
              <div>
                <Label>Skip Reason *</Label>
                <Input
                  value={editInspForm.skip_reason}
                  onChange={(e) => setEditInspForm({ ...editInspForm, skip_reason: e.target.value })}
                  placeholder="Why is the batch skipping intermediate stages?"
                  data-testid="edit-insp-skip-reason-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInspOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitEditPendingBatch} disabled={busy} data-testid="edit-insp-submit">
              <Pencil className="w-4 h-4 mr-1.5" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assembly Materials Issue dialog (Phase 28) */}
      <Dialog open={asmDialogOpen} onOpenChange={setAsmDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="issue-materials-dialog">
          <DialogHeader>
            <DialogTitle>
              Issue Materials → {asmTarget?.code}
              {asmPreview && <div className="text-xs font-normal text-slate-500 mt-1">Assembling {asmPreview.current_qty} × <strong>{asmPreview.item_code}</strong> — {asmPreview.item_name} · debits from <strong>SFG Store</strong></div>}
            </DialogTitle>
          </DialogHeader>
          {!asmPreview ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading BOM…</div>
          ) : asmPreview.components.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">
              No BOM configured on <strong>{asmPreview.item_code}</strong>. Add components in Items → BOM before issuing.
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {Object.keys(asmShortages).length > 0 && (
                <div className="rounded border border-red-300 bg-red-50 text-red-900 text-xs p-3" data-testid="asm-shortage-banner">
                  <div className="font-semibold mb-1">Cannot issue — {Object.keys(asmShortages).length} component(s) short in SFG Store:</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {Object.values(asmShortages).map((s) => (
                      <li key={s.component_code}>
                        <span className="mono">{s.component_code}</span> — need {s.required}, have {s.available}, <strong>short by {s.short_by}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <table className="w-full text-sm" data-testid="issue-materials-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                    <th className="py-2 px-2">Component</th>
                    <th className="py-2 px-2 text-right">Per Unit</th>
                    <th className="py-2 px-2 text-right">SFG Stock</th>
                    <th className="py-2 px-2 text-right">Qty to Issue *</th>
                  </tr>
                </thead>
                <tbody>
                  {asmPreview.components.map((c, idx) => {
                    const line = asmLines[idx] || { qty: 0 };
                    const insufficient = Number(line.qty) > c.sfg_store_stock + 1e-6;
                    const shortInfo = asmShortages[c.component_code];  // Phase 30 — after failed submit
                    const flagged = insufficient || !!shortInfo;
                    return (
                      <tr key={c.component_item_id} className={`border-t ${flagged ? "bg-red-50 border-red-200" : "border-slate-100"}`} data-testid={`asm-row-${c.component_code}`}>
                        <td className="py-2 px-2">
                          <div className="mono text-[11px] text-slate-500">{c.component_code}</div>
                          <div className="text-slate-800 text-sm">{c.component_name}</div>
                        </td>
                        <td className="py-2 px-2 text-right mono text-xs text-slate-600">{c.per_unit} {c.uom}</td>
                        <td className={`py-2 px-2 text-right mono text-xs ${flagged ? "text-red-600 font-semibold" : "text-slate-700"}`} data-testid={`asm-stock-${c.component_code}`}>
                          {c.sfg_store_stock} {c.uom}
                          {shortInfo && (
                            <div className="text-[10px] text-red-700 mt-0.5" data-testid={`asm-shortage-${c.component_code}`}>
                              short by <strong>{shortInfo.short_by}</strong>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            type="number"
                            className={`h-8 text-right ${flagged ? "border-red-300" : ""}`}
                            value={line.qty}
                            onChange={(e) => {
                              const v = e.target.value;
                              setAsmLines((prev) => prev.map((p, i) => (i === idx ? { ...p, qty: v } : p)));
                            }}
                            data-testid={`asm-qty-${c.component_code}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div>
                <Label>Remarks</Label>
                <Input value={asmRemarks} onChange={(e) => setAsmRemarks(e.target.value)} placeholder="e.g. batch shift-A" data-testid="asm-remarks-input" />
              </div>
              <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
                On confirm, each component qty above will be <strong>debited from SFG Store</strong> and credited to Assembly Store. Movements are logged in Stock Ledger.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsmDialogOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={busy || !asmPreview || asmPreview.components.length === 0} onClick={submitIssueMaterials} data-testid="asm-submit-btn">
              <Package className="w-4 h-4 mr-1.5" /> Confirm Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>{active?.code} — Stage History</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(active.history || []).length === 0 && (
                <div className="text-center text-slate-500 text-sm py-6">No stage transitions yet.</div>
              )}
              {(active.history || []).map((h, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-3 text-xs" data-testid={`history-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-800">
                      {STAGE_LABEL[h.stage_from] || h.stage_from} <ArrowRight className="inline w-3 h-3" /> {STAGE_LABEL[h.stage_to] || h.stage_to}
                    </div>
                    <div className="text-slate-400 mono text-[10px]">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-2 text-[11px]">
                    <div><span className="text-slate-500">In:</span> <span className="mono">{h.input_qty}</span></div>
                    <div><span className="text-emerald-700">Passed:</span> <span className="mono">{h.output_qty}</span></div>
                    <div><span className="text-red-600">Failed:</span> <span className="mono">{h.rejected_qty || 0}</span></div>
                    <div><span className="text-amber-700">Waste:</span> <span className="mono">{h.wastage_qty || 0}</span></div>
                  </div>
                  {(h.output_item_code || h.dest_store) && (
                    <div className="mt-2 flex items-center gap-2 text-[11px] flex-wrap">
                      {h.output_item_code && (
                        <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-blue-800 mono" data-testid={`history-output-${idx}`}>
                          <Package className="inline w-2.5 h-2.5 mr-1" />
                          {h.output_item_code}
                        </span>
                      )}
                      {h.dest_store && (
                        <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-slate-700" data-testid={`history-store-${idx}`}>
                          → {h.dest_store}
                        </span>
                      )}
                    </div>
                  )}
                  {h.notes && <div className="text-slate-600 mt-1 italic">{h.notes}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">By {h.by || "—"}</div>
                </div>
              ))}
              {(active.material_issues || []).length > 0 && (
                <div className="pt-3 mt-3 border-t border-slate-200">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Assembly Material Issues</div>
                  {active.material_issues.map((mi, idx) => (
                    <div key={idx} className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 text-xs mb-2" data-testid={`material-issue-row-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-indigo-800">
                          <Package className="inline w-3 h-3 mr-1" /> Issued from SFG Store
                        </div>
                        <div className="text-slate-400 mono text-[10px]">{mi.at ? new Date(mi.at).toLocaleString() : ""}</div>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {mi.components.map((c, ci) => (
                          <div key={ci} className="flex items-center justify-between text-[11px]">
                            <span><span className="mono text-slate-600">{c.component_code}</span> · {c.component_name}</span>
                            <span className="mono text-slate-800">{c.qty} {c.uom || ""}</span>
                          </div>
                        ))}
                      </div>
                      {mi.remarks && <div className="text-slate-600 mt-1 italic">{mi.remarks}</div>}
                      <div className="text-[10px] text-slate-400 mt-1">By {mi.by || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 35.5 — Job Work DC dialog (Send Outside): every eligible material
          at this stage is laid out as a list (one row per item), pre-selected —
          the operator only edits quantity, or unchecks a row to leave it out. */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-5xl" data-testid="send-outside-dialog">
          <DialogHeader>
            <DialogTitle>Create Job Work DC — {STAGE_LABEL[scStage] || ""}</DialogTitle>
          </DialogHeader>
          {scStage && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  {scJobs.length} item{scJobs.length === 1 ? "" : "s"} at {STAGE_LABEL[scStage]}
                </div>
                {scJobs.length > 0 && (
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={toggleScSelectAll} data-testid="sc-select-all-toggle">
                    {allScJobsSelected ? "Clear all" : "Select all"}
                  </button>
                )}
              </div>
              {scJobs.length === 0 ? (
                <div className="text-xs text-slate-500 italic text-center py-6" data-testid="sc-job-list">No eligible items at this stage — everything is either already sent outside or has no remaining quantity.</div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-[420px] overflow-y-auto overflow-x-auto">
                    <table className="w-full text-sm" data-testid="sc-job-list">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                          <th className="py-2 px-3 w-8"></th>
                          <th className="py-2 px-3">Job ID</th>
                          <th className="py-2 px-3">Item</th>
                          <th className="py-2 px-3 text-right">Available</th>
                          <th className="py-2 px-3 w-28">Qty to Send *</th>
                          <th className="py-2 px-3 w-56">Return as part #</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scJobs.map((j) => {
                          const it = itemMap[j.current_item_id] || itemMap[j.fg_item_id] || itemMap[j.raw_item_id] || {};
                          const checked = Object.prototype.hasOwnProperty.call(scSelection, j.id);
                          const outputItemId = checked ? scSelection[j.id]?.output_item_id : defaultOutputItemId(j);
                          return (
                            <tr key={j.id} className={`border-t border-slate-100 ${checked ? "bg-orange-50/40" : ""}`} data-testid={`sc-job-row-${j.code}`}>
                              <td className="py-2 px-3 align-top">
                                <Checkbox checked={checked} onCheckedChange={() => toggleScJob(j)} data-testid={`sc-job-check-${j.code}`} />
                              </td>
                              <td className="py-2 px-3 align-top mono text-xs text-slate-500 whitespace-nowrap">{j.code}</td>
                              <td className="py-2 px-3 align-top min-w-[160px]">
                                <div className="text-sm font-medium text-slate-800" title={it.name}>{it.name || "—"}</div>
                                <div className="mono text-[10px] text-slate-400">{it.code || "—"}</div>
                              </td>
                              <td className="py-2 px-3 align-top text-right mono text-xs text-slate-700 whitespace-nowrap">{j.current_qty}</td>
                              <td className="py-2 px-3 align-top">
                                <Input
                                  type="number"
                                  className="h-8 text-xs"
                                  disabled={!checked}
                                  value={checked ? scSelection[j.id]?.qty : j.current_qty}
                                  onChange={(e) => setScJobQty(j.id, e.target.value)}
                                  data-testid={`sc-job-qty-${j.code}`}
                                />
                              </td>
                              <td className="py-2 px-3 align-top min-w-[200px]">
                                <Select value={outputItemId || ""} onValueChange={(v) => setScJobOutputItem(j.id, v)} disabled={!checked}>
                                  <SelectTrigger className="h-8 text-xs min-w-0" data-testid={`sc-job-output-select-${j.code}`}><SelectValue className="truncate" placeholder="Same part" /></SelectTrigger>
                                  <SelectContent className="max-h-72">
                                    {scDcItemOptions}
                                  </SelectContent>
                                </Select>
                                {outputItemId && outputItemId !== (j.current_item_id || j.fg_item_id || j.raw_item_id) && (
                                  <div className="text-[10px] text-orange-700 mt-0.5">Changes part # on receipt</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div>
                <Label>Vendor *</Label>
                <Select value={scForm.vendor_id} onValueChange={(v) => setScForm((f) => ({ ...f, vendor_id: v }))}>
                  <SelectTrigger data-testid="sc-vendor-select"><SelectValue placeholder="Select a vendor…" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.filter((s) => s.status !== "blacklisted").map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`sc-vendor-opt-${s.code}`}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle No.</Label>
                <Input value={scForm.vehicle_no} onChange={(e) => setScForm((f) => ({ ...f, vehicle_no: e.target.value }))} data-testid="sc-vehicle-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={scForm.notes} onChange={(e) => setScForm((f) => ({ ...f, notes: e.target.value }))} data-testid="sc-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={submitSendOutside} disabled={busy} data-testid="sc-send-submit">
              <Send className="w-4 h-4 mr-1" /> Send &amp; Generate DC{Object.keys(scSelection).length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-stage page — "Receive" button: every pending-arrival job at
          this stage in one list, instead of hunting for each card. */}
      <Dialog open={receiveStageOpen} onOpenChange={setReceiveStageOpen}>
        <DialogContent className="max-w-lg" data-testid="receive-stage-dialog">
          <DialogHeader>
            <DialogTitle>Receive — {STAGE_LABEL[receiveStageKey] || ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="receive-stage-list">
            {receiveStageJobs.length === 0 && (
              <div className="text-xs text-slate-500 italic text-center py-6">Nothing waiting to be received at this stage.</div>
            )}
            {receiveStageJobs.map((j) => {
              const it = itemMap[j.current_item_id] || itemMap[j.fg_item_id] || itemMap[j.raw_item_id] || {};
              return (
                <div key={j.id} className="flex items-center gap-3 border border-cyan-200 bg-cyan-50/40 rounded-md px-3 py-2" data-testid={`receive-stage-row-${j.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="mono text-[11px] text-slate-500">{j.code}</div>
                    <div className="text-sm font-medium text-slate-800 truncate">{it.name || "—"} <span className="mono text-slate-400">({it.code || "—"})</span></div>
                    <div className="text-[11px] text-slate-500 mono">{j.current_qty} {it.uom || ""}</div>
                  </div>
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 shrink-0" onClick={() => receiveJob(j)} disabled={busy} data-testid={`receive-stage-btn-${j.code}`}>
                    <Package className="w-3.5 h-3.5 mr-1" /> Receive
                  </Button>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveStageOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-stage list — "Edit" (pencil icon): corrects only the
          Remaining quantity at this stage. Adjusts the stage store's
          inventory by the same delta and logs it to the Stock Ledger. */}
      <Dialog open={adjustQtyOpen} onOpenChange={setAdjustQtyOpen}>
        <DialogContent className="max-w-sm" data-testid="adjust-qty-dialog">
          <DialogHeader>
            <DialogTitle>Edit Quantity — {adjustQtyTarget?.code}</DialogTitle>
          </DialogHeader>
          {adjustQtyTarget && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Current remaining: <span className="mono font-semibold text-slate-700">{adjustQtyTarget.current_qty}</span>
              </div>
              <div>
                <Label>New Remaining Quantity *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={adjustQtyForm.new_qty}
                  onChange={(e) => setAdjustQtyForm((f) => ({ ...f, new_qty: e.target.value }))}
                  data-testid="adjust-qty-input"
                />
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={adjustQtyForm.reason}
                  onChange={(e) => setAdjustQtyForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. recount, damaged pieces"
                  data-testid="adjust-qty-reason-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Only the quantity at this stage changes. The stage store's stock is adjusted by the same amount and logged in the Stock Ledger.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustQtyOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitAdjustQty} disabled={busy} data-testid="adjust-qty-submit">
              <Pencil className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Material page — "Create Job Work DC" / per-row "Send Outside":
          a manual (job-less) subcontract DC, same list layout as the
          job-based Create Job Work DC dialog. */}
      <Dialog open={rawDcOpen} onOpenChange={setRawDcOpen}>
        <DialogContent className="max-w-5xl" data-testid="raw-dc-dialog">
          <DialogHeader>
            <DialogTitle>Create Job Work DC — Raw Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {rawDcEligible.length} raw material item{rawDcEligible.length === 1 ? "" : "s"}
              </div>
            </div>
            {rawDcEligible.length === 0 ? (
              <div className="text-xs text-slate-500 italic text-center py-6" data-testid="raw-dc-item-list">No eligible raw material — everything is already sent outside.</div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <div className="max-h-[420px] overflow-y-auto overflow-x-auto">
                  <table className="w-full text-sm" data-testid="raw-dc-item-list">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                        <th className="py-2 px-3 w-8"></th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3 text-right">Available</th>
                        <th className="py-2 px-3 w-28">Qty to Send *</th>
                        <th className="py-2 px-3 w-56">Return as part #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawDcEligible.map((r) => {
                        const checked = Object.prototype.hasOwnProperty.call(rawDcSelection, r.item_id);
                        const outputItemId = checked ? rawDcSelection[r.item_id]?.output_item_id : r.item_id;
                        return (
                          <tr key={r.item_id} className={`border-t border-slate-100 ${checked ? "bg-orange-50/40" : ""}`} data-testid={`raw-dc-row-${r.item.code}`}>
                            <td className="py-2 px-3 align-top">
                              <Checkbox checked={checked} onCheckedChange={() => toggleRawDcItem(r.item_id, r.qty)} data-testid={`raw-dc-check-${r.item.code}`} />
                            </td>
                            <td className="py-2 px-3 align-top min-w-[160px]">
                              <div className="text-sm font-medium text-slate-800" title={r.item.name}>{r.item.name || "—"}</div>
                              <div className="mono text-[10px] text-slate-400">{r.item.code || "—"}</div>
                            </td>
                            <td className="py-2 px-3 align-top text-right mono text-xs text-slate-700 whitespace-nowrap">{r.qty}</td>
                            <td className="py-2 px-3 align-top">
                              <Input
                                type="number"
                                className="h-8 text-xs"
                                disabled={!checked}
                                value={checked ? rawDcSelection[r.item_id]?.qty : r.qty}
                                onChange={(e) => setRawDcItemQty(r.item_id, e.target.value)}
                                data-testid={`raw-dc-qty-${r.item.code}`}
                              />
                            </td>
                            <td className="py-2 px-3 align-top min-w-[200px]">
                              <Select value={outputItemId || ""} onValueChange={(v) => setRawDcOutputItem(r.item_id, v)} disabled={!checked}>
                                <SelectTrigger className="h-8 text-xs min-w-0" data-testid={`raw-dc-output-select-${r.item.code}`}><SelectValue className="truncate" placeholder="Same part" /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                  {rawDcItemOptions}
                                </SelectContent>
                              </Select>
                              {outputItemId && outputItemId !== r.item_id && (
                                <div className="text-[10px] text-orange-700 mt-0.5">Changes part # on receipt</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div>
              <Label>Vendor *</Label>
              <Select value={rawDcForm.vendor_id} onValueChange={(v) => setRawDcForm((f) => ({ ...f, vendor_id: v }))}>
                <SelectTrigger data-testid="raw-dc-vendor-select"><SelectValue placeholder="Select a vendor…" /></SelectTrigger>
                <SelectContent>
                  {suppliers.filter((s) => s.status !== "blacklisted").map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`raw-dc-vendor-opt-${s.code}`}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vehicle No.</Label>
              <Input value={rawDcForm.vehicle_no} onChange={(e) => setRawDcForm((f) => ({ ...f, vehicle_no: e.target.value }))} data-testid="raw-dc-vehicle-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={rawDcForm.notes} onChange={(e) => setRawDcForm((f) => ({ ...f, notes: e.target.value }))} data-testid="raw-dc-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRawDcOpen(false)}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={submitRawMaterialDc} disabled={busy} data-testid="raw-dc-submit">
              <Send className="w-4 h-4 mr-1" /> Send &amp; Generate DC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Material page — "Edit": corrects RM Store qty directly. */}
      <Dialog open={rawEditOpen} onOpenChange={setRawEditOpen}>
        <DialogContent className="max-w-sm" data-testid="raw-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Quantity — {rawEditTarget?.item?.code}</DialogTitle>
          </DialogHeader>
          {rawEditTarget && (
            <div className="space-y-3 text-sm">
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Current RM Store qty: <span className="mono font-semibold text-slate-700">{rawEditTarget.qty} {rawEditTarget.item?.uom}</span>
              </div>
              <div>
                <Label>New Quantity *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rawEditForm.new_qty}
                  onChange={(e) => setRawEditForm((f) => ({ ...f, new_qty: e.target.value }))}
                  data-testid="raw-edit-qty-input"
                />
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  value={rawEditForm.reason}
                  onChange={(e) => setRawEditForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. recount, damaged pieces"
                  data-testid="raw-edit-reason-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Adjusts RM Store stock directly and logs the correction in the Stock Ledger.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRawEditOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitRawEdit} disabled={busy} data-testid="raw-edit-submit">
              <Pencil className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 35.4 — Goods Received dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="sc-receive-dialog">
          <DialogHeader>
            <DialogTitle>Goods Received — {scDc?.code}</DialogTitle>
          </DialogHeader>
          {scDc && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
                <div><span className="text-slate-500">DC:</span> <span className="mono font-semibold text-slate-800">{scDc.code}</span></div>
                <div><span className="text-slate-500">Vendor:</span> <strong>{scDc.vendor_name}</strong></div>
                <div><span className="text-slate-500">Production Order:</span> <span className="mono">{scDc.job_code}</span></div>
                <div><span className="text-slate-500">Stage:</span> <strong>{scDc.stage_label}</strong></div>
              </div>
              {(scDc.lines || []).map((l, idx) => {
                const outputId = l.output_item_id;
                return (
                  <div key={idx} className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`sc-receive-line-${idx}`}>
                    <div className="text-xs space-y-1">
                      <div><span className="text-slate-500">Sent as:</span> <strong>{itemMap[l.item_id]?.name || l.part_name || "—"}</strong> <span className="mono text-slate-500">({itemMap[l.item_id]?.code || l.part_code || "—"})</span></div>
                      {outputId && outputId !== l.item_id ? (
                        <div><span className="text-slate-500">Receiving as:</span> <strong className="text-orange-700">{itemMap[outputId]?.name || l.output_item_name || "—"}</strong> <span className="mono text-slate-500">({itemMap[outputId]?.code || l.output_item_code || "—"})</span></div>
                      ) : null}
                      <div>
                        <span className="text-slate-500">Sent Qty:</span> <span className="mono font-semibold">{l.qty}</span>
                        {dcLineReceivedQty(l) > 0 && (
                          <>
                            <span className="text-slate-500 ml-2">Received:</span> <span className="mono">{fmtQty(dcLineReceivedQty(l))}</span>
                            <span className="text-slate-500 ml-2">Remaining:</span> <span className="mono font-semibold">{fmtQty(dcLinePendingQty(l))}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {isDcLineReceived(scDc, l) ? (
                      <div className="text-xs text-emerald-700">
                        Fully received on GIN <span className="mono">{dcLineGinCode(scDc, l)}</span>
                      </div>
                    ) : (
                    <div>
                      <Label>Received Quantity * <span className="text-[11px] font-normal text-slate-500">(max {fmtQty(dcLinePendingQty(l))})</span></Label>
                      <Input
                        type="number"
                        min="0"
                        max={dcLinePendingQty(l)}
                        value={scReceiveForm.received_qtys[idx] ?? ""}
                        onChange={(e) => setScReceiveForm((f) => ({ ...f, received_qtys: f.received_qtys.map((q, i) => (i === idx ? e.target.value : q)) }))}
                        data-testid={idx === 0 ? "sc-received-qty-input" : `sc-received-qty-input-${idx}`}
                      />
                      {!(l.is_manual && !l.item_id) && (
                        <div className="mt-2">
                          <ReceivedPartPicker
                            value={scReceiveForm.received_item_ids?.[idx] || ""}
                            items={items}
                            onPick={(v) => setScReceiveForm((f) => ({ ...f, received_item_ids: f.received_item_ids.map((x, i) => (i === idx ? v : x)) }))}
                            targetStore={scReceiveForm.target_stores?.[idx] || ""}
                            onTargetStore={(v) => setScReceiveForm((f) => ({ ...f, target_stores: (f.target_stores || []).map((x, i) => (i === idx ? v : x)) }))}
                            testId={idx === 0 ? "sc-received-part" : `sc-received-part-${idx}`}
                          />
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                );
              })}
              <div className="text-[11px] text-slate-500">
                A GIN will be created for the lines with a received qty, and a Quality Inspection entry will be queued automatically.
                Any qty not received now stays pending — the DC stays open for another GIN until its full qty has been received.
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={scReceiveForm.notes} onChange={(e) => setScReceiveForm((f) => ({ ...f, notes: e.target.value }))} data-testid="sc-receive-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitReceiveGoods} disabled={busy} data-testid="sc-receive-submit">
              <Package className="w-4 h-4 mr-1" /> Receive &amp; Generate GIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
